import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  accountRequiresCostingCode,
  clearMovementPosting,
  clearReconciliationSeal,
  createManualReconciliationMatch,
  fetchAccountReconciliation,
  fetchCompanies,
  fetchLoanInstallmentPreview,
  fetchLoans,
  fetchOpenInvoicesByCardCode,
  fetchPostingPreview,
  markMovementPostedManual,
  postLoanInstallmentToSap,
  getLeasingPostingStatus,
  postMovementToSap,
  reopenLeasingInstallmentAsPartial,
  runAccountReconciliation,
  deleteReconciliationMatch,
  searchBusinessPartnersByName,
  type BusinessPartnerHit,
  type Company,
  type Loan,
  type LoanInstallmentPreview,
  type LoanInstallmentLineProposed,
  type PostingCandidateInvoice,
  type PostingChoice,
  type PostingPreview,
  type ReconciliationSnapshot,
  type ReconciliationMatch,
  type Movement,
  type SapEntry,
} from "../api";
import { formatDateEs, formatIban, formatMoney } from "../lib/grouping";

/**
 * Vista de CONCILIACIÓN por cuenta. Muestra dos tablas en paralelo:
 *   - Izquierda: movimientos bancarios (Wealth Reader) con badge conciliado/pendiente.
 *   - Derecha:   apuntes contables SAP con badge conciliado/pendiente.
 *
 * Botón "Conciliar automáticamente" dispara el matcher del backend (1:1, mismo
 * signo, importe exacto, ±7 días). El estado se persiste en disco.
 */
/** Columnas de las dos tablas y sus anchos por defecto (px). Los índices se
 *  usan para redimensionar y para persistir en localStorage. */
const BANK_COLS = ["Fecha", "Descripción", "Transferencia", "Importe", "Saldo", "Estado"] as const;
const SAP_COLS = ["Fecha", "Asiento · Memo", "Importe", "Saldo", "Estado"] as const;
const DEFAULT_BANK_WIDTHS = [95, 260, 220, 105, 115, 140];
const DEFAULT_SAP_WIDTHS = [95, 320, 105, 115, 90];
const LS_KEY_WIDTHS_BANK = "reconciliation.bankColWidths";
const LS_KEY_WIDTHS_SAP = "reconciliation.sapColWidths";
const LS_KEY_SPLIT = "reconciliation.splitPercent";

/** Clave única de un candidato de factura abierta. Fuente + docEntry
 *  identifica de forma inequívoca una fila en la lista (una PurchaseInvoice
 *  puede compartir docEntry con una Invoice: por eso incluyo la source). */
function invoiceKey(c: { source: string; docEntry: number }): string {
  return `${c.source}#${c.docEntry}`;
}

function loadWidths(key: string, fallback: number[]): number[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback.slice();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return fallback.slice();
    const arr = parsed.map((v) => (typeof v === "number" && v >= 40 ? v : 0));
    if (arr.length !== fallback.length || arr.some((v) => v === 0)) return fallback.slice();
    return arr;
  } catch {
    return fallback.slice();
  }
}

function loadSplitPercent(): number {
  try {
    const raw = localStorage.getItem(LS_KEY_SPLIT);
    const n = raw !== null ? Number(raw) : NaN;
    if (Number.isFinite(n) && n >= 20 && n <= 80) return n;
  } catch {
    /* ignore */
  }
  return 50;
}

/** Reduce el string de búsqueda a una forma comparable: lowercase, sin
 *  espacios laterales, coma→punto (para importes tipo 1.523,47 → 1523.47) */
function normalizeSearchToken(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, " ").replace(/,/g, ".");
}

/** Devuelve true si el término se encuentra dentro del blob. Vacío = pasa. */
function matchesSearch(term: string, blob: string): boolean {
  const t = normalizeSearchToken(term);
  if (!t) return true;
  return blob.includes(t);
}

/** String contra el que buscar en un movimiento del banco: fecha ISO,
 *  fecha española DD/MM/YYYY, importe con y sin decimales, saldo, descripción,
 *  senderReceiver. */
function movementSearchBlob(m: Movement): string {
  const parts: string[] = [];
  if (m.operationDate) {
    parts.push(m.operationDate); // 2026-06-26
    // 26/06/2026 (español)
    const d = m.operationDate.slice(8, 10);
    const mo = m.operationDate.slice(5, 7);
    const y = m.operationDate.slice(0, 4);
    if (d && mo && y) parts.push(`${d}/${mo}/${y}`);
  }
  const abs = Math.abs(m.amount);
  parts.push(m.amount.toFixed(2));
  parts.push(abs.toFixed(2));
  if (m.balance !== null) parts.push(m.balance.toFixed(2));
  if (m.description) parts.push(m.description);
  if (m.transferDetails?.concept) parts.push(m.transferDetails.concept);
  if (m.transferDetails?.senderReceiver) parts.push(m.transferDetails.senderReceiver);
  if (m.transferDetails?.accountNumber) parts.push(m.transferDetails.accountNumber);
  return normalizeSearchToken(parts.join(" "));
}

/** String contra el que buscar en un apunte SAP. */
function sapSearchBlob(e: SapEntry): string {
  const parts: string[] = [];
  if (e.referenceDate) {
    parts.push(e.referenceDate);
    const d = e.referenceDate.slice(8, 10);
    const mo = e.referenceDate.slice(5, 7);
    const y = e.referenceDate.slice(0, 4);
    if (d && mo && y) parts.push(`${d}/${mo}/${y}`);
  }
  const abs = Math.abs(e.amount);
  parts.push(e.amount.toFixed(2));
  parts.push(abs.toFixed(2));
  parts.push(String(e.jdtNum));
  parts.push(String(e.lineId));
  if (e.lineMemo) parts.push(e.lineMemo);
  if (e.headerMemo) parts.push(e.headerMemo);
  return normalizeSearchToken(parts.join(" "));
}

export function AccountReconciliation() {
  const { iban: ibanParam } = useParams<{ iban: string }>();
  const iban = ibanParam ?? "";
  // Ruta y etiqueta del enlace "Volver" del breadcrumb. Se toma de
  // `location.state.from` (que se pasa desde el link Conciliar en Resumen /
  // Cuentas / Movimientos / Apuntes SAP). Fallback: `/cuentas`.
  const location = useLocation();
  const backTo = (location.state as { from?: string } | null)?.from ?? "/cuentas";
  const backLabel = backTo === "/resumen" ? "← Resumen" : "← Cuentas";

  const [data, setData] = useState<ReconciliationSnapshot | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [runBusy, setRunBusy] = useState(false);
  const [runMsg, setRunMsg] = useState<string | null>(null);
  const [sealBusy, setSealBusy] = useState(false);
  const [filter, setFilter] = useState<"all" | "pending" | "matched">("all");
  const [bankSearch, setBankSearch] = useState("");
  const [sapSearch, setSapSearch] = useState("");
  const [postingUuid, setPostingUuid] = useState<string | null>(null);
  /** Selección MÚLTIPLE para conciliación manual: el usuario marca N
   *  movimientos y M apuntes SAP y pulsa "Casar seleccionados". El
   *  frontend genera el producto cartesiano (N × M enlaces). Esto cubre
   *  casos reales como "2 movimientos suman lo mismo que 1 apunte SAP",
   *  "1 movimiento troceado en 3 líneas SAP", etc. */
  const [selectedMovementUuids, setSelectedMovementUuids] = useState<Set<string>>(new Set());
  const [selectedSapKeys, setSelectedSapKeys] = useState<Set<string>>(new Set());
  const [manualBusy, setManualBusy] = useState(false);
  const [manualMsg, setManualMsg] = useState<string | null>(null);

  const toggleMovementSelection = useCallback((uuid: string) => {
    setSelectedMovementUuids((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  }, []);
  const toggleSapSelection = useCallback((key: string) => {
    setSelectedSapKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // --- Layout ajustable por el usuario --------------------------------------
  const [bankWidths, setBankWidths] = useState<number[]>(() =>
    loadWidths(LS_KEY_WIDTHS_BANK, DEFAULT_BANK_WIDTHS),
  );
  const [sapWidths, setSapWidths] = useState<number[]>(() =>
    loadWidths(LS_KEY_WIDTHS_SAP, DEFAULT_SAP_WIDTHS),
  );
  const [splitPercent, setSplitPercent] = useState<number>(() => loadSplitPercent());
  const splitContainerRef = useRef<HTMLDivElement | null>(null);

  // Persistencia: cada vez que cambian los anchos o el split, se guardan.
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY_WIDTHS_BANK, JSON.stringify(bankWidths));
    } catch {
      /* ignore quota */
    }
  }, [bankWidths]);
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY_WIDTHS_SAP, JSON.stringify(sapWidths));
    } catch {
      /* ignore quota */
    }
  }, [sapWidths]);
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY_SPLIT, String(splitPercent));
    } catch {
      /* ignore quota */
    }
  }, [splitPercent]);

  // Activa modo ancho del layout mientras esta pantalla está montada. Sin esto
  // el .main global tiene max-width: 1400px y las tablas van estrechísimas.
  useEffect(() => {
    document.body.classList.add("main-wide");
    return () => document.body.classList.remove("main-wide");
  }, []);

  /** Arranca un drag de columna. Se lee el ancho actual y en cada mousemove
   *  se actualiza. mouseup libera los listeners. Ancho mínimo 40px para no
   *  hacer desaparecer la columna. */
  const startColResize = useCallback(
    (which: "bank" | "sap", colIdx: number, event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const startX = event.clientX;
      const setter = which === "bank" ? setBankWidths : setSapWidths;
      const startWidths = which === "bank" ? bankWidths : sapWidths;
      const startW = startWidths[colIdx] ?? 100;
      const target = event.currentTarget as HTMLElement;
      target.classList.add("dragging");
      const onMove = (ev: MouseEvent) => {
        const delta = ev.clientX - startX;
        const next = Math.max(40, startW + delta);
        setter((prev) => {
          const copy = prev.slice();
          copy[colIdx] = next;
          return copy;
        });
      };
      const onUp = () => {
        target.classList.remove("dragging");
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [bankWidths, sapWidths],
  );

  /** Arranca el drag del separador entre las dos tablas. Se calcula el % del
   *  ancho del contenedor donde cae el ratón; clamp entre 20% y 80%. */
  const startSplitResize = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const container = splitContainerRef.current;
    if (!container) return;
    const target = event.currentTarget as HTMLElement;
    target.classList.add("dragging");
    const onMove = (ev: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      if (rect.width <= 0) return;
      const pct = ((ev.clientX - rect.left) / rect.width) * 100;
      setSplitPercent(Math.max(20, Math.min(80, pct)));
    };
    const onUp = () => {
      target.classList.remove("dragging");
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, []);

  const resetLayout = () => {
    setBankWidths(DEFAULT_BANK_WIDTHS.slice());
    setSapWidths(DEFAULT_SAP_WIDTHS.slice());
    setSplitPercent(50);
  };

  const load = async () => {
    if (!iban) return;
    setError(null);
    setLoading(true);
    try {
      const [snap, comps] = await Promise.all([
        fetchAccountReconciliation(iban),
        fetchCompanies(),
      ]);
      setData(snap);
      setCompanies(comps.items);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iban]);

  const doRun = async () => {
    if (!iban || runBusy) return;
    setRunBusy(true);
    setRunMsg(null);
    try {
      const r = await runAccountReconciliation(iban);
      const sealPart = r.balanceSealDate
        ? ` 🔒 Sellado por saldo hasta ${formatDateEs(r.balanceSealDate)} (${r.sealedMovements} mov / ${r.sealedSapEntries} SAP).`
        : "";
      setRunMsg(
        `✓ ${r.newAutoMatches} emparejamiento${r.newAutoMatches === 1 ? "" : "s"} nuevo${r.newAutoMatches === 1 ? "" : "s"}. ` +
          `Total conciliados: ${r.totalMatches}. Pendientes: ${r.pendingMovements} banco / ${r.pendingSapEntries} SAP.` +
          sealPart,
      );
      await load();
    } catch (e: unknown) {
      setRunMsg(`❌ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setRunBusy(false);
    }
  };

  /** Anula el "Casado por saldo" de la cuenta. Los matches 1:1 (manuales o
   *  auto ya persistidos) se conservan. Todo lo que estaba por saldo pasa a
   *  Pendiente. Si vuelves a "Conciliar automáticamente" y los saldos siguen
   *  cuadrando, el sello puede volver a recalcularse en el mismo punto. */
  const doClearSeal = async () => {
    if (!iban || sealBusy || !data?.balanceSealDate) return;
    const seal = data.balanceSealDate;
    if (
      !confirm(
        `Se va a anular el "Casado por saldo hasta ${formatDateEs(seal)}".\n\n` +
          `Todos los movimientos y apuntes SAP hasta esa fecha volverán a aparecer como Pendientes.\n` +
          `Los emparejamientos 1:1 (los que llevan tick) se conservan.\n\n` +
          `¿Continuar?`,
      )
    ) {
      return;
    }
    setSealBusy(true);
    setRunMsg(null);
    try {
      const cleared = await clearReconciliationSeal(iban);
      setRunMsg(
        cleared
          ? `✓ Sellado por saldo anulado (era hasta ${formatDateEs(cleared)}). Revisa los pendientes.`
          : `✓ No había sellado por saldo activo.`,
      );
      await load();
    } catch (e: unknown) {
      setRunMsg(`❌ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSealBusy(false);
    }
  };

  /** Deshace UN enlace específico (N:M). Si `link` es null, deshace TODOS
   *  los enlaces del movimiento. */
  const unmatch = async (
    movementUuid: string,
    link: { jdtNum: number; lineId: number } | null,
  ) => {
    if (!iban) return;
    const label = link
      ? `Deshacer el enlace con SAP ${link.jdtNum}·L${link.lineId}?`
      : "Deshacer TODOS los enlaces manuales de este movimiento?";
    if (!confirm(label)) return;
    try {
      await deleteReconciliationMatch(iban, movementUuid, link ?? undefined);
      await load();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : String(e));
    }
  };

  /** Crea el producto cartesiano de enlaces entre los N movimientos y los M
   *  apuntes SAP seleccionados. Recorre secuencialmente (no en paralelo) para
   *  que un fallo en medio se refleje con la primera N-1 ya persistidas.
   *  El backend admite N:M sin restricción; sólo devuelve 500 si algo falla. */
  const doManualMatch = async () => {
    if (!iban || manualBusy) return;
    const movUuids = [...selectedMovementUuids];
    const sapKeys = [...selectedSapKeys];
    if (movUuids.length === 0 || sapKeys.length === 0) return;
    const totalLinks = movUuids.length * sapKeys.length;
    if (totalLinks > 6 && !confirm(
      `Se van a crear ${totalLinks} enlaces (${movUuids.length} movimientos × ${sapKeys.length} apuntes SAP). ¿Continuar?`,
    )) {
      return;
    }
    setManualBusy(true);
    setManualMsg(`⏳ Creando ${totalLinks} enlace${totalLinks === 1 ? "" : "s"}…`);
    let created = 0;
    try {
      for (const uuid of movUuids) {
        for (const key of sapKeys) {
          const [jdtStr, lineStr] = key.split("#");
          const jdtNum = Number(jdtStr);
          const lineId = Number(lineStr);
          if (!Number.isFinite(jdtNum) || !Number.isFinite(lineId)) continue;
          await createManualReconciliationMatch(iban, { movementUuid: uuid, jdtNum, lineId });
          created++;
        }
      }
      setManualMsg(`✓ ${created} enlace${created === 1 ? "" : "s"} guardado${created === 1 ? "" : "s"}.`);
      setSelectedMovementUuids(new Set());
      setSelectedSapKeys(new Set());
      await load();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setManualMsg(`❌ ${msg} (${created} de ${totalLinks} guardados)`);
    } finally {
      setManualBusy(false);
    }
  };

  const currency = data?.account.currency || "EUR";

  /** Saldo acumulado SAP por línea = suma de amount desde el apunte más
   *  antiguo hasta ese apunte incluido. Se calcula sobre TODOS los apuntes
   *  (no sobre la lista filtrada) para que la columna sea coherente cuando
   *  el usuario cambia de filtro. Clave = `${jdtNum}#${lineId}`. */
  const sapRunningByKey = useMemo(() => {
    const map = new Map<string, number>();
    if (!data) return map;
    // data.sapEntries viene en orden `referenceDate` DESC — recorremos al revés.
    let acc = 0;
    for (let i = data.sapEntries.length - 1; i >= 0; i--) {
      const e = data.sapEntries[i]!;
      acc += e.amount;
      map.set(`${e.jdtNum}#${e.lineId}`, acc);
    }
    return map;
  }, [data]);

  const { visibleMovements, visibleSap } = useMemo(() => {
    if (!data) return { visibleMovements: [] as Movement[], visibleSap: [] as SapEntry[] };
    const seal = data.balanceSealDate;
    const isMovMatched = (m: Movement) =>
      (data.matchesByMovementUuid[m.uuid]?.length ?? 0) > 0;
    const isSapMatched = (e: SapEntry) =>
      (data.matchesBySapKey[`${e.jdtNum}#${e.lineId}`]?.length ?? 0) > 0;
    const isMovSealed = (m: Movement) =>
      seal !== null && (m.operationDate ?? "") <= seal;
    const isSapSealed = (e: SapEntry) =>
      seal !== null && (e.referenceDate ?? "") <= seal;

    // Filtro por estado (all/pending/matched).
    let movs = data.movements;
    let saps = data.sapEntries;
    if (filter === "pending") {
      movs = movs.filter((m) => !isMovMatched(m) && !isMovSealed(m));
      saps = saps.filter((e) => !isSapMatched(e) && !isSapSealed(e));
    } else if (filter === "matched") {
      movs = movs.filter((m) => isMovMatched(m) || isMovSealed(m));
      saps = saps.filter((e) => isSapMatched(e) || isSapSealed(e));
    }

    // Buscador por fecha o importe (también acepta trozos de descripción).
    // El término se compara contra un string compuesto por fila (fecha ISO,
    // fecha en formato español DD/MM/YYYY, importe con y sin signo, memo,
    // descripción). Case-insensitive; normaliza coma → punto para importes.
    if (bankSearch.trim()) {
      movs = movs.filter((m) => matchesSearch(bankSearch, movementSearchBlob(m)));
    }
    if (sapSearch.trim()) {
      saps = saps.filter((e) => matchesSearch(sapSearch, sapSearchBlob(e)));
    }

    return { visibleMovements: movs, visibleSap: saps };
  }, [data, filter, bankSearch, sapSearch]);

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2 style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span>Conciliación · {data?.account.alias || formatIban(iban)}</span>
            {data?.account.companyId && (() => {
              const c = companies.find((x) => x.id === data.account.companyId);
              const label = c ? (c.alias || c.name) : data.account.companyId;
              return <span className="badge company-badge">{label}</span>;
            })()}
          </h2>
          <p className="meta">
            <Link to={backTo}>{backLabel}</Link>
            {"  ·  "}
            <Link
              to={`/cuentas/${encodeURIComponent(iban)}/movimientos`}
              state={{ from: backTo }}
            >
              Movimientos
            </Link>
            {"  ·  "}
            <Link
              to={`/cuentas/${encodeURIComponent(iban)}/apuntes-sap`}
              state={{ from: backTo }}
            >
              Apuntes SAP
            </Link>
            {data?.account && (
              <>
                {"  ·  "}
                <code>{formatIban(data.account.iban)}</code>
                {"  ·  "}
                {data.account.bankName || data.account.bankCode}
                {data.account.sapGLAccount && (
                  <>
                    {"  ·  G/L "}
                    <code>{data.account.sapGLAccount}</code>
                  </>
                )}
              </>
            )}
          </p>
          {data?.account && (
            <p className="meta" style={{ margin: "4px 0 0 0" }}>
              🔄 Apuntes SAP actualizados:{" "}
              {data.account.sapEntriesLastSyncedAt ? (
                <strong>
                  {new Date(data.account.sapEntriesLastSyncedAt).toLocaleString("es-ES")}
                </strong>
              ) : (
                <span className="meta">nunca sincronizado</span>
              )}
              {data.account.sapEntriesLastError && (
                <>
                  {"  ·  "}
                  <span className="error">
                    último error: {data.account.sapEntriesLastError}
                  </span>
                </>
              )}
            </p>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={doRun}
            disabled={runBusy || !data}
            title="Ejecuta el matcher: mismo signo, importe exacto, ±7 días, relación 1:1"
          >
            {runBusy ? "…" : "↻ Conciliar automáticamente"}
          </button>
          <button type="button" onClick={load} disabled={loading}>
            {loading ? "Cargando…" : "Recargar"}
          </button>
        </div>
      </div>

      {data && (
        <div
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <span className="badge ok">
            Conciliados {data.totals.matches}
          </span>
          {data.balanceSealDate && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "2px 4px 2px 8px",
                borderRadius: 10,
                fontSize: 12,
                fontWeight: 500,
                background: "#e3f2fd",
                color: "#0d47a1",
                border: "1px solid #90caf9",
              }}
              title="Todos los movimientos y apuntes con fecha igual o anterior a esta fecha están casados por saldo (el saldo del banco coincide con el saldo del mayor SAP)."
            >
              🔒 Casado por saldo hasta {formatDateEs(data.balanceSealDate)}
              {" · "}
              {data.totals.sealedMovements} mov / {data.totals.sealedSapEntries} SAP
              <button
                type="button"
                onClick={doClearSeal}
                disabled={sealBusy}
                title="Anula el casado por saldo: todo lo anterior a la fecha del sello vuelve a aparecer como Pendiente. Los emparejamientos 1:1 se conservan."
                style={{
                  padding: "1px 8px",
                  fontSize: 11,
                  lineHeight: "16px",
                  background: "#fff",
                  border: "1px solid #90caf9",
                  color: "#0d47a1",
                  borderRadius: 8,
                }}
              >
                {sealBusy ? "…" : "Anular"}
              </button>
            </span>
          )}
          <span className="badge warn">
            Banco pendiente {data.totals.pendingMovements}
          </span>
          <span className="badge warn">
            SAP pendiente {data.totals.pendingSapEntries}
          </span>
          <span className="meta">
            Total banco {data.totals.movements} · Total SAP {data.totals.sapEntries}
          </span>
          <span style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
            <FilterButton current={filter} value="all" onClick={setFilter}>
              Todos
            </FilterButton>
            <FilterButton current={filter} value="pending" onClick={setFilter}>
              Pendientes
            </FilterButton>
            <FilterButton current={filter} value="matched" onClick={setFilter}>
              Conciliados
            </FilterButton>
          </span>
        </div>
      )}

      {runMsg && (
        <p className={runMsg.startsWith("❌") ? "error" : "meta"}>{runMsg}</p>
      )}

      {/* Barra de conciliación MANUAL: aparece cuando hay algo seleccionado
          (o hay mensaje). Permite selección múltiple en ambas tablas y
          muestra las sumas para que veas si cuadran antes de casar. */}
      {data && (selectedMovementUuids.size > 0 || selectedSapKeys.size > 0 || manualMsg) && (() => {
        const movs = data.movements.filter((m) => selectedMovementUuids.has(m.uuid));
        const saps = data.sapEntries.filter((e) =>
          selectedSapKeys.has(`${e.jdtNum}#${e.lineId}`),
        );
        const sumMov = movs.reduce((a, m) => a + m.amount, 0);
        const sumSap = saps.reduce((a, e) => a + e.amount, 0);
        const diff = Math.round((sumMov - sumSap) * 100) / 100;
        const cuadra = movs.length > 0 && saps.length > 0 && diff === 0;
        const totalLinks = selectedMovementUuids.size * selectedSapKeys.size;
        return (
          <div
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
              padding: "0.4rem 0.75rem",
              marginBottom: 12,
              border: "1px solid var(--border, #444)",
              borderRadius: 6,
              background: cuadra ? "rgba(60,180,120,0.10)" : "rgba(80,160,255,0.05)",
              flexWrap: "wrap",
              fontSize: "0.9em",
            }}
          >
            <strong>Conciliar manualmente:</strong>
            <span>
              Banco: <code>{selectedMovementUuids.size}</code>
              {movs.length > 0 && (
                <span className="meta"> · Σ {formatMoney(sumMov, currency)}</span>
              )}
            </span>
            <span>
              SAP: <code>{selectedSapKeys.size}</code>
              {saps.length > 0 && (
                <span className="meta"> · Σ {formatMoney(sumSap, currency)}</span>
              )}
            </span>
            {movs.length > 0 && saps.length > 0 && (
              <span
                className={cuadra ? "badge ok" : "badge warn"}
                title="Diferencia entre suma banco y suma SAP"
              >
                Δ {formatMoney(diff, currency)}
              </span>
            )}
            <span className="meta">
              → {totalLinks} enlace{totalLinks === 1 ? "" : "s"}
            </span>
            <button
              type="button"
              onClick={doManualMatch}
              disabled={
                manualBusy ||
                selectedMovementUuids.size === 0 ||
                selectedSapKeys.size === 0
              }
            >
              {manualBusy ? "…" : "Casar seleccionados"}
            </button>
            <button
              type="button"
              onClick={() => {
                setSelectedMovementUuids(new Set());
                setSelectedSapKeys(new Set());
                setManualMsg(null);
              }}
              disabled={manualBusy}
              style={{ fontSize: "0.85em" }}
            >
              Limpiar selección
            </button>
            {manualMsg && (
              <span className={manualMsg.startsWith("❌") ? "error" : "meta"}>
                {manualMsg}
              </span>
            )}
          </div>
        );
      })()}

      {error && <p className="error">{error}</p>}
      {!error && data === null && !loading && <p>Cargando…</p>}

      {data && (
        <div
          ref={splitContainerRef}
          style={{
            display: "grid",
            gridTemplateColumns: `${splitPercent}% 8px calc(${100 - splitPercent}% - 8px)`,
            alignItems: "start",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: 0,
              }}
            >
              <h3 style={{ margin: 0 }}>Movimientos banco (Wealth Reader)</h3>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input
                  type="text"
                  value={bankSearch}
                  onChange={(e) => setBankSearch(e.target.value)}
                  placeholder="🔍 fecha o importe"
                  title="Filtra por fecha (DD/MM/AAAA o AAAA-MM-DD), importe (con o sin decimales) o texto de la descripción"
                  style={{ fontSize: "0.8em", padding: "0.15rem 0.5rem", width: "10rem" }}
                />
                {bankSearch && (
                  <button
                    type="button"
                    onClick={() => setBankSearch("")}
                    style={{ fontSize: "0.75em", padding: "0.1rem 0.4rem" }}
                    title="Limpiar búsqueda"
                  >
                    ✕
                  </button>
                )}
                <button
                  type="button"
                  onClick={resetLayout}
                  style={{ fontSize: "0.75em", padding: "0.1rem 0.5rem" }}
                  title="Restablecer anchos y división de tablas"
                >
                  ⟲ Layout
                </button>
              </div>
            </div>
            {visibleMovements.length === 0 ? (
              <p className="meta">Sin movimientos que mostrar con este filtro.</p>
            ) : (
              <div
                style={{
                  height: "calc(100vh - 260px)",
                  overflowY: "auto",
                  overflowX: "auto",
                  border: "1px solid var(--line-200, #ddd)",
                  borderRadius: 4,
                }}
              >
                <table className="data" style={{ tableLayout: "fixed", width: bankWidths.reduce((a, b) => a + b, 0) }}>
                  <colgroup>
                    {bankWidths.map((w, i) => (
                      <col key={i} style={{ width: `${w}px` }} />
                    ))}
                  </colgroup>
                  <thead className="sticky-head">
                    <tr>
                      {BANK_COLS.map((label, i) => (
                        <th
                          key={label}
                          className={"resizable" + (i === 3 || i === 4 ? " num" : "")}
                        >
                          {label}
                          <span
                            className="col-resizer"
                            onMouseDown={(e) => startColResize("bank", i, e)}
                            title="Arrastra para redimensionar la columna"
                          />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleMovements.map((m) => (
                      <MovementRow
                        key={m.uuid}
                        movement={m}
                        matches={data.matchesByMovementUuid[m.uuid] ?? []}
                        sealed={
                          data.balanceSealDate !== null &&
                          (m.operationDate ?? "") <= data.balanceSealDate
                        }
                        currency={currency}
                        selected={selectedMovementUuids.has(m.uuid)}
                        onSelect={() => toggleMovementSelection(m.uuid)}
                        onUnmatch={unmatch}
                        onPost={() => setPostingUuid(m.uuid)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div
            className="split-handle"
            onMouseDown={startSplitResize}
            title="Arrastra para cambiar el ancho de las dos tablas"
            style={{ alignSelf: "stretch", minHeight: 60 }}
          />

          <div style={{ minWidth: 0 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: 0,
              }}
            >
              <h3 style={{ margin: 0 }}>Apuntes contables SAP</h3>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input
                  type="text"
                  value={sapSearch}
                  onChange={(e) => setSapSearch(e.target.value)}
                  placeholder="🔍 fecha o importe"
                  title="Filtra por fecha (DD/MM/AAAA o AAAA-MM-DD), importe (con o sin decimales), memo o número de asiento"
                  style={{ fontSize: "0.8em", padding: "0.15rem 0.5rem", width: "10rem" }}
                />
                {sapSearch && (
                  <button
                    type="button"
                    onClick={() => setSapSearch("")}
                    style={{ fontSize: "0.75em", padding: "0.1rem 0.4rem" }}
                    title="Limpiar búsqueda"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
            {visibleSap.length === 0 ? (
              <p className="meta">Sin apuntes SAP que mostrar con este filtro.</p>
            ) : (
              <div
                style={{
                  height: "calc(100vh - 260px)",
                  overflowY: "auto",
                  overflowX: "auto",
                  border: "1px solid var(--line-200, #ddd)",
                  borderRadius: 4,
                }}
              >
                <table className="data" style={{ tableLayout: "fixed", width: sapWidths.reduce((a, b) => a + b, 0) }}>
                  <colgroup>
                    {sapWidths.map((w, i) => (
                      <col key={i} style={{ width: `${w}px` }} />
                    ))}
                  </colgroup>
                  <thead className="sticky-head">
                    <tr>
                      {SAP_COLS.map((label, i) => (
                        <th
                          key={label}
                          className={"resizable" + (i === 2 || i === 3 ? " num" : "")}
                          title={i === 3 ? "Saldo acumulado desde el primer apunte" : undefined}
                        >
                          {label}
                          <span
                            className="col-resizer"
                            onMouseDown={(e) => startColResize("sap", i, e)}
                            title="Arrastra para redimensionar la columna"
                          />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleSap.map((e) => {
                      const key = `${e.jdtNum}#${e.lineId}`;
                      const linkMatches = data.matchesBySapKey[key] ?? [];
                      return (
                        <SapRow
                          key={key}
                          entry={e}
                          matches={linkMatches}
                          sealed={
                            data.balanceSealDate !== null &&
                            (e.referenceDate ?? "") <= data.balanceSealDate
                          }
                          currency={currency}
                          runningBalance={sapRunningByKey.get(key) ?? null}
                          selected={selectedSapKeys.has(key)}
                          onSelect={() => toggleSapSelection(key)}
                        />
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {postingUuid && iban && (
        <PostingModal
          iban={iban}
          movementUuid={postingUuid}
          currency={currency}
          companyId={data?.account.companyId ?? null}
          onClose={() => setPostingUuid(null)}
          onPosted={async () => {
            setPostingUuid(null);
            await load();
          }}
        />
      )}
    </section>
  );
}

function FilterButton({
  current,
  value,
  onClick,
  children,
}: {
  current: "all" | "pending" | "matched";
  value: "all" | "pending" | "matched";
  onClick: (v: "all" | "pending" | "matched") => void;
  children: React.ReactNode;
}) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      style={{
        fontSize: "0.85em",
        padding: "0.2rem 0.6rem",
        opacity: active ? 1 : 0.7,
        fontWeight: active ? 600 : 400,
      }}
    >
      {children}
    </button>
  );
}

function MovementRow({
  movement,
  matches,
  sealed,
  currency,
  selected,
  onSelect,
  onUnmatch,
  onPost,
}: {
  movement: Movement;
  matches: ReconciliationMatch[];
  sealed: boolean;
  currency: string;
  selected: boolean;
  onSelect: () => void;
  onUnmatch: (
    movementUuid: string,
    link: { jdtNum: number; lineId: number } | null,
  ) => void;
  onPost: () => void;
}) {
  const amountClass = movement.amount < 0 ? "num neg" : "num";
  const desc = movement.description || "";
  const isMatched = matches.length > 0;
  return (
    <tr
      onClick={onSelect}
      style={{
        cursor: "pointer",
        background: selected ? "rgba(80,160,255,0.18)" : undefined,
        fontSize: "0.85em",
        lineHeight: 1.15,
      }}
    >
      <td style={{ whiteSpace: "nowrap", padding: "0.25rem 0.5rem" }}>
        <input
          type="checkbox"
          checked={selected}
          onChange={onSelect}
          onClick={(e) => e.stopPropagation()}
          style={{ marginRight: 4, verticalAlign: "middle" }}
          title="Seleccionar para conciliar manualmente (permite N:M)"
        />
        {formatDateEs(movement.operationDate) || <span className="meta">—</span>}
      </td>
      <td style={{ padding: "0.25rem 0.5rem" }}>
        <div
          title={desc}
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {desc || <span className="meta">—</span>}
        </div>
      </td>
      <td style={{ padding: "0.25rem 0.5rem" }}>
        {movement.transferDetails ? (
          <div
            title={[
              movement.transferDetails.concept,
              movement.transferDetails.senderReceiver,
              movement.transferDetails.accountNumber,
            ]
              .filter(Boolean)
              .join(" · ")}
            style={{ fontSize: "0.95em", lineHeight: 1.2 }}
          >
            {movement.transferDetails.concept && (
              <div
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {movement.transferDetails.concept}
              </div>
            )}
            {movement.transferDetails.senderReceiver && (
              <div
                className="meta"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {movement.transferDetails.senderReceiver}
              </div>
            )}
            {movement.transferDetails.accountNumber && (
              <div
                className="meta"
                style={{
                  fontSize: "0.85em",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {movement.transferDetails.accountNumber}
              </div>
            )}
          </div>
        ) : (
          <span className="meta">—</span>
        )}
      </td>
      <td className={amountClass} style={{ whiteSpace: "nowrap", padding: "0.25rem 0.5rem" }}>
        {formatMoney(movement.amount, currency)}
      </td>
      <td className="num" style={{ whiteSpace: "nowrap", padding: "0.25rem 0.5rem" }}>
        {movement.balance !== null
          ? formatMoney(movement.balance, currency)
          : <span className="meta">—</span>}
      </td>
      <td style={{ padding: "0.25rem 0.5rem" }}>
        {isMatched ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 3, alignItems: "center" }}>
            {matches.map((match) => (
              <span
                key={`${match.jdtNum}#${match.lineId}`}
                className="badge ok"
                title={`SAP ${match.jdtNum}·L${match.lineId} (${match.method})`}
                style={{ display: "inline-flex", alignItems: "center", gap: 2 }}
              >
                ✓ {match.jdtNum}·L{match.lineId}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onUnmatch(movement.uuid, { jdtNum: match.jdtNum, lineId: match.lineId });
                  }}
                  style={{
                    marginLeft: 2,
                    fontSize: "0.7em",
                    padding: "0 0.25rem",
                    lineHeight: 1,
                  }}
                  title="Deshacer solo este enlace"
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        ) : sealed ? (
          <span
            style={{
              display: "inline-block",
              padding: "1px 6px",
              borderRadius: 8,
              fontSize: 11,
              fontWeight: 500,
              background: "#e3f2fd",
              color: "#0d47a1",
              border: "1px solid #90caf9",
            }}
            title="Casado por saldo: el saldo del banco coincide con el saldo del mayor SAP en una fecha posterior."
          >
            🔒 Por saldo
          </span>
        ) : (
          <>
            <span className="badge warn">Pendiente</span>{" "}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onPost();
              }}
              style={{ fontSize: "0.75em", padding: "0.05rem 0.4rem" }}
              title="Contabilizar en SAP según la regla del banco"
            >
              Contab…
            </button>
          </>
        )}
      </td>
    </tr>
  );
}

function SapRow({
  entry,
  matches,
  sealed,
  currency,
  runningBalance,
  selected,
  onSelect,
}: {
  entry: SapEntry;
  matches: ReconciliationMatch[];
  sealed: boolean;
  currency: string;
  runningBalance: number | null;
  selected: boolean;
  onSelect: () => void;
}) {
  const amountClass = entry.amount < 0 ? "num neg" : "num";
  const memo = entry.lineMemo || entry.headerMemo;
  const isMatched = matches.length > 0;
  return (
    <tr
      onClick={onSelect}
      style={{
        cursor: "pointer",
        background: selected ? "rgba(80,160,255,0.18)" : undefined,
        fontSize: "0.85em",
        lineHeight: 1.15,
      }}
    >
      <td style={{ whiteSpace: "nowrap", padding: "0.25rem 0.5rem" }}>
        <input
          type="checkbox"
          checked={selected}
          onChange={onSelect}
          onClick={(e) => e.stopPropagation()}
          style={{ marginRight: 4, verticalAlign: "middle" }}
          title="Seleccionar para conciliar manualmente (permite N:M)"
        />
        {formatDateEs(entry.referenceDate) || <span className="meta">—</span>}
      </td>
      <td style={{ padding: "0.25rem 0.5rem", maxWidth: 260 }}>
        <div
          title={`${entry.jdtNum}·L${entry.lineId}${memo ? ` · ${memo}` : ""}`}
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          <code>{entry.jdtNum}</code>
          <span className="meta">·L{entry.lineId}</span>
          {memo && <span> · {memo}</span>}
        </div>
      </td>
      <td className={amountClass} style={{ whiteSpace: "nowrap", padding: "0.25rem 0.5rem" }}>
        {formatMoney(entry.amount, entry.currency || currency)}
      </td>
      <td className="num" style={{ whiteSpace: "nowrap", padding: "0.25rem 0.5rem" }}>
        {runningBalance !== null
          ? formatMoney(runningBalance, entry.currency || currency)
          : <span className="meta">—</span>}
      </td>
      <td style={{ whiteSpace: "nowrap", padding: "0.25rem 0.5rem" }}>
        {isMatched ? (
          <span
            className="badge ok"
            title={
              matches.length === 1
                ? `Casado con movimiento ${matches[0]!.movementUuid.slice(0, 8)}…`
                : `Casado con ${matches.length} movimientos`
            }
          >
            ✓{matches.length > 1 ? ` ×${matches.length}` : ""}
          </span>
        ) : sealed ? (
          <span
            style={{
              display: "inline-block",
              padding: "1px 6px",
              borderRadius: 8,
              fontSize: 11,
              fontWeight: 500,
              background: "#e3f2fd",
              color: "#0d47a1",
              border: "1px solid #90caf9",
            }}
            title="Casado por saldo: el saldo del banco coincide con el saldo del mayor SAP en una fecha posterior."
          >
            🔒 Por saldo
          </span>
        ) : (
          <span className="badge warn">Pendiente</span>
        )}
      </td>
    </tr>
  );
}

// -----------------------------------------------------------------------------
// Sub-flujo "Ya está contabilizado en SAP": marcar el movimiento como
// conciliado apuntando a un pago/asiento existente sin volver a crearlo.
// Uso: cuando SAP creó el documento pero la app falló al finalizar el match
// (típico: JdtNum no venía en la respuesta del SL en su día).
// -----------------------------------------------------------------------------

function MarkPostedManualSection({
  iban,
  movementUuid,
  onPosted,
  onClose,
}: {
  iban: string;
  movementUuid: string;
  onPosted: () => Promise<void> | void;
  onClose: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [documentType, setDocumentType] = useState<
    "vendor_payment" | "incoming_payment" | "journal_entry"
  >("vendor_payment");
  const [docNum, setDocNum] = useState<string>("");
  const [docEntry, setDocEntry] = useState<string>("");
  const [memo, setMemo] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const dn = docNum.trim() ? Number(docNum) : undefined;
      const de = docEntry.trim() ? Number(docEntry) : undefined;
      if (!dn && !de) {
        throw new Error("Indica DocNum (visible en SAP) o DocEntry (interno).");
      }
      const r = await markMovementPostedManual({
        iban,
        movementUuid,
        documentType,
        ...(dn ? { docNum: dn } : {}),
        ...(de ? { docEntry: de } : {}),
        memo: memo.trim() || undefined,
      });
      alert(`✓ Movimiento marcado como contabilizado. JdtNum ${r.jdtNum}.`);
      await onPosted();
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!expanded) {
    return (
      <div style={{ marginTop: 12, textAlign: "right" }}>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          style={{ fontSize: "0.85em" }}
          title="Si el pago/asiento ya existe en SAP y solo quieres vincular el movimiento sin duplicarlo"
        >
          ¿Ya está contabilizado en SAP? Marcar manualmente
        </button>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 12, padding: 12, background: "rgba(80,160,255,0.08)", borderRadius: 6 }}>
      <strong>Marcar como contabilizado en SAP</strong>
      <p className="meta" style={{ margin: "6px 0 10px 0", fontSize: "0.85em" }}>
        Vincula este movimiento a un pago o asiento que YA existe en SAP.
        No crea nada nuevo. Útil cuando la contabilización automática falló a
        posteriori (ej. SAP creó el pago pero la app no supo leer el JdtNum).
      </p>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
        <label>
          <div className="meta">Tipo de documento</div>
          <select
            value={documentType}
            onChange={(e) =>
              setDocumentType(e.target.value as "vendor_payment" | "incoming_payment" | "journal_entry")
            }
            disabled={busy}
          >
            <option value="vendor_payment">Pago a proveedor (OVPM)</option>
            <option value="incoming_payment">Cobro de cliente (ORCT)</option>
            <option value="journal_entry">Asiento contable (OJDT)</option>
          </select>
        </label>
        <label>
          <div className="meta">DocNum (visible en SAP)</div>
          <input
            value={docNum}
            onChange={(e) => setDocNum(e.target.value.replace(/[^\d]/g, ""))}
            placeholder="ej. 26110087"
            style={{ width: "9rem" }}
            disabled={busy}
          />
        </label>
        <label>
          <div className="meta">DocEntry (opcional)</div>
          <input
            value={docEntry}
            onChange={(e) => setDocEntry(e.target.value.replace(/[^\d]/g, ""))}
            placeholder="si lo sabes"
            style={{ width: "8rem" }}
            disabled={busy}
          />
        </label>
      </div>
      <label style={{ display: "block", marginBottom: 8 }}>
        <div className="meta">Memo (opcional, máx 50)</div>
        <input
          value={memo}
          onChange={(e) => setMemo(e.target.value.slice(0, 50))}
          placeholder="Se usa la descripción del movimiento si se deja vacío"
          style={{ width: "100%" }}
          disabled={busy}
        />
      </label>
      {error && <p className="error" style={{ marginTop: 6 }}>{error}</p>}
      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
        <button type="button" onClick={() => setExpanded(false)} disabled={busy}>Cancelar</button>
        <button type="button" onClick={submit} disabled={busy}>
          {busy ? "Marcando…" : "Marcar como contabilizado"}
        </button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Sub-flujo MANUAL: cuando el backend no detecta el préstamo automáticamente
// (por texto ni por importe), el usuario elige el préstamo del dropdown y
// mete a mano los importes. Reutiliza el mismo POST /post-loan-installment,
// así que la validación (cuadre, cuentas obligatorias, mismo banco) sigue
// siendo la del backend.
// -----------------------------------------------------------------------------

type ManualLine = {
  role: LoanInstallmentLineProposed["role"];
  accountCode: string;
  costingCode: string | null;
  debit: number;
  credit: number;
  memo: string;
};

function LoanInstallmentManualSection({
  iban,
  movementUuid,
  currency,
  onPosted,
  onClose,
}: {
  iban: string;
  movementUuid: string;
  currency: string;
  onPosted: () => Promise<void> | void;
  onClose: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [loans, setLoans] = useState<Loan[] | null>(null);
  const [bankCode, setBankCode] = useState<string>("");
  const [bankGL, setBankGL] = useState<string>("");
  const [absAmount, setAbsAmount] = useState<number>(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [chosenKey, setChosenKey] = useState<string>("");
  const [lines, setLines] = useState<ManualLine[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!expanded) return;
    let cancelled = false;
    (async () => {
      setLoadError(null);
      try {
        const [accRes, mvRes, loansRes] = await Promise.all([
          fetch(`/api/accounts/${encodeURIComponent(iban)}`).then((r) => r.json()) as Promise<{ bankCode: string; sapGLAccount: string }>,
          fetch(`/api/accounts/${encodeURIComponent(iban)}/movements/${encodeURIComponent(movementUuid)}`).then((r) => r.json()) as Promise<Movement>,
          fetchLoans(),
        ]);
        if (cancelled) return;
        setBankCode(accRes.bankCode);
        setBankGL(accRes.sapGLAccount || "");
        setAbsAmount(Math.abs(mvRes.amount));
        const filtered = loansRes.items.filter(
          (l) => l.active && l.bankCode === accRes.bankCode && l.sapAccountShortTerm && l.sapAccountInterest,
        );
        setLoans(filtered);
      } catch (e: unknown) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [expanded, iban, movementUuid]);

  const chosen = useMemo(
    () => loans?.find((l) => `${l.bankCode}:${l.contractNumber}` === chosenKey) ?? null,
    [loans, chosenKey],
  );

  // Al elegir un préstamo, pre-cargamos las líneas con sus cuentas configuradas
  // y el banco al haber = |amount|. Los importes de las cuentas del préstamo
  // quedan a 0 — el usuario los mete a mano hasta que se cuadre.
  useEffect(() => {
    if (!chosen) {
      setLines([]);
      return;
    }
    const defaults: ManualLine[] = [
      {
        role: "principal_cp",
        accountCode: chosen.sapAccountShortTerm,
        costingCode: chosen.costingCodeShortTerm || null,
        debit: 0,
        credit: 0,
        memo: "",
      },
      {
        role: "interest",
        accountCode: chosen.sapAccountInterest,
        costingCode: chosen.costingCodeInterest || null,
        debit: 0,
        credit: 0,
        memo: "",
      },
      {
        role: "bank",
        accountCode: bankGL,
        costingCode: null,
        debit: 0,
        credit: absAmount,
        memo: "",
      },
    ];
    if (chosen.sapAccountLongTerm) {
      defaults.push({
        role: "reclass_lp",
        accountCode: chosen.sapAccountLongTerm,
        costingCode: chosen.costingCodeLongTerm || null,
        debit: 0,
        credit: 0,
        memo: "",
      });
      defaults.push({
        role: "reclass_cp",
        accountCode: chosen.sapAccountShortTerm,
        costingCode: chosen.costingCodeShortTerm || null,
        debit: 0,
        credit: 0,
        memo: "",
      });
    }
    if (chosen.sapAccountFees) {
      defaults.push({
        role: "fees",
        accountCode: chosen.sapAccountFees,
        costingCode: chosen.costingCodeFees || null,
        debit: 0,
        credit: 0,
        memo: "",
      });
    }
    setLines(defaults);
  }, [chosen, bankGL, absAmount]);

  if (!expanded) {
    return (
      <div style={{ marginTop: 12 }}>
        <button type="button" onClick={() => setExpanded(true)}>
          Contabilizar manualmente como cuota de préstamo →
        </button>
        <span className="meta" style={{ marginLeft: 8, fontSize: "0.85em" }}>
          El detector automático no encontró el préstamo. Puedes elegirlo a mano.
        </span>
      </div>
    );
  }

  if (loadError) return <p className="error">Error cargando préstamos: {loadError}</p>;
  if (loans === null) return <p className="meta">Cargando préstamos…</p>;

  const debitTotal = lines.reduce((a, l) => a + (Number.isFinite(l.debit) ? l.debit : 0), 0);
  const creditTotal = lines.reduce((a, l) => a + (Number.isFinite(l.credit) ? l.credit : 0), 0);
  const diff = Math.round((debitTotal - creditTotal) * 100) / 100;
  const balanced = Math.abs(diff) < 0.01;

  const updateLine = (idx: number, patch: Partial<ManualLine>) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const submit = async () => {
    if (!chosen) return;
    if (!balanced) {
      setError(`Asiento descuadrado: Debe=${debitTotal.toFixed(2)} Haber=${creditTotal.toFixed(2)}`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await postLoanInstallmentToSap({
        iban,
        movementUuid,
        loanBankCode: chosen.bankCode,
        loanContractNumber: chosen.contractNumber,
        lines: lines.map((l) => ({
          accountCode: l.accountCode,
          costingCode: l.costingCode ?? undefined,
          debit: l.debit,
          credit: l.credit,
          memo: l.memo,
        })),
      });
      alert(`✓ Cuota contabilizada. JdtNum ${r.jdtNum}.`);
      await onPosted();
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 12, padding: 12, background: "rgba(80,160,255,0.08)", borderRadius: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <strong>Contabilizar como cuota (manual)</strong>
        <span className="meta">banco {bankCode}</span>
      </div>
      {loans.length === 0 ? (
        <p className="meta" style={{ marginTop: 8 }}>
          No hay préstamos activos con CP + Interés configurados en el banco <code>{bankCode}</code>.
          Configura las cuentas contables del préstamo primero.
        </p>
      ) : (
        <>
          <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <label>Préstamo:</label>
            <select
              value={chosenKey}
              onChange={(e) => setChosenKey(e.target.value)}
              disabled={busy}
              style={{ minWidth: 240 }}
            >
              <option value="">— Elige préstamo —</option>
              {loans.map((l) => {
                const key = `${l.bankCode}:${l.contractNumber}`;
                return (
                  <option key={key} value={key}>
                    {l.alias || l.contractNumber} · {l.contractNumber}
                  </option>
                );
              })}
            </select>
          </div>
          {chosen && (
            <>
              <table className="data" style={{ marginTop: 8, fontSize: "0.85em" }}>
                <thead>
                  <tr>
                    <th>Rol</th>
                    <th>Cuenta</th>
                    <th>CC</th>
                    <th className="num">Debe</th>
                    <th className="num">Haber</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={i}>
                      <td><span className="meta">{ROLE_LABEL[l.role]}</span></td>
                      <td>
                        <input
                          value={l.accountCode}
                          onChange={(e) => updateLine(i, { accountCode: e.target.value })}
                          style={{ width: "7rem" }}
                          disabled={busy}
                        />
                      </td>
                      <td>
                        <input
                          value={l.costingCode ?? ""}
                          onChange={(e) => updateLine(i, { costingCode: e.target.value || null })}
                          style={{ width: "6rem" }}
                          disabled={busy}
                          placeholder={accountRequiresCostingCode(l.accountCode) ? "obligatorio" : ""}
                        />
                      </td>
                      <td className="num">
                        <input
                          type="number"
                          step="0.01"
                          value={l.debit}
                          onChange={(e) => updateLine(i, { debit: Number(e.target.value) || 0 })}
                          style={{ width: "6rem", textAlign: "right" }}
                          disabled={busy}
                        />
                      </td>
                      <td className="num">
                        <input
                          type="number"
                          step="0.01"
                          value={l.credit}
                          onChange={(e) => updateLine(i, { credit: Number(e.target.value) || 0 })}
                          style={{ width: "6rem", textAlign: "right" }}
                          disabled={busy}
                        />
                      </td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: "1px solid rgba(128,128,128,0.4)" }}>
                    <td colSpan={3} className="meta" style={{ textAlign: "right" }}>Totales</td>
                    <td className="num"><strong>{debitTotal.toFixed(2)}</strong></td>
                    <td className="num"><strong>{creditTotal.toFixed(2)}</strong></td>
                  </tr>
                </tbody>
              </table>
              {!balanced && (
                <p className="error" style={{ marginTop: 6 }}>
                  Descuadrado: diferencia {diff.toFixed(2)} {currency}. Rellena los importes hasta que Debe = Haber.
                </p>
              )}
              {error && <p className="error" style={{ marginTop: 6 }}>{error}</p>}
              <div style={{ marginTop: 10, display: "flex", gap: 6, justifyContent: "flex-end" }}>
                <button type="button" onClick={() => setExpanded(false)} disabled={busy}>Cancelar</button>
                <button type="button" onClick={submit} disabled={busy || !balanced}>
                  {busy ? "Enviando…" : "Enviar a SAP"}
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Sub-flujo: contabilizar como cuota de préstamo
// -----------------------------------------------------------------------------

const ROLE_LABEL: Record<LoanInstallmentLineProposed["role"], string> = {
  principal_cp: "Capital CP",
  interest: "Intereses",
  fees: "Comisiones",
  reclass_lp: "Reclasif. LP",
  reclass_cp: "Reclasif. CP",
  bank: "Banco",
};

/**
 * Se pinta dentro del `PostingModal` cuando el preview normal devuelve
 * `no_matching_rule`. Lanza un segundo fetch contra `loan-installment-preview`
 * y, si detecta un préstamo cuya cuota casa con el movimiento, ofrece un
 * mini-formulario de 3-6 líneas editables para lanzar el asiento a SAP.
 */
function LoanInstallmentSection({
  iban,
  movementUuid,
  currency,
  onPosted,
  onClose,
}: {
  iban: string;
  movementUuid: string;
  currency: string;
  onPosted: () => Promise<void> | void;
  onClose: () => void;
}) {
  const [preview, setPreview] = useState<LoanInstallmentPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [chosenLoanKey, setChosenLoanKey] = useState<string>("");
  const [expanded, setExpanded] = useState(false);
  const [lines, setLines] = useState<LoanInstallmentLineProposed[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPreview = useCallback(
    async (loanKey?: string) => {
      setLoading(true);
      setError(null);
      try {
        const p = await fetchLoanInstallmentPreview(iban, movementUuid, loanKey);
        setPreview(p);
        if (p.ok && p.proposed) {
          setLines(p.proposed.lines.map((l) => ({ ...l })));
        }
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    },
    [iban, movementUuid],
  );

  useEffect(() => {
    void loadPreview();
  }, [loadPreview]);

  if (loading) {
    return <p className="meta" style={{ marginTop: 8 }}>Buscando préstamo…</p>;
  }
  if (!preview) return null;

  if (!preview.ok) {
    // Si el backend no ha detectado préstamo (por texto ni por importe),
    // ofrecemos el modo MANUAL: dropdown de préstamos activos del banco y
    // líneas del asiento a rellenar a mano.
    if (preview.reason === "no_loan_detected") {
      return (
        <LoanInstallmentManualSection
          iban={iban}
          movementUuid={movementUuid}
          currency={currency}
          onPosted={onPosted}
          onClose={onClose}
        />
      );
    }
    return (
      <div style={{ marginTop: 12, padding: 10, background: "rgba(80,160,255,0.06)", borderRadius: 6 }}>
        <p className="meta" style={{ margin: 0 }}>{preview.message}</p>
      </div>
    );
  }

  // Detectado, pero necesita elegir préstamo (>1 candidato).
  if (!preview.chosenLoan) {
    return (
      <div style={{ marginTop: 12, padding: 10, background: "rgba(80,160,255,0.06)", borderRadius: 6 }}>
        <p style={{ margin: "0 0 6px 0" }}>
          Se detectaron varios préstamos en la descripción. Elige cuál:
        </p>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {preview.loans.map((l) => {
            const key = `${l.bankCode}:${l.contractNumber}`;
            return (
              <button
                key={key}
                type="button"
                onClick={() => {
                  setChosenLoanKey(key);
                  void loadPreview(key);
                }}
              >
                {l.alias || l.contractNumber}
              </button>
            );
          })}
        </div>
        {chosenLoanKey && <p className="meta">Cargando cuota del préstamo…</p>}
      </div>
    );
  }

  const loanLabel = preview.chosenLoan.alias || preview.chosenLoan.contractNumber;

  if (!preview.installmentMatch.ok) {
    const reason = preview.installmentMatch.reason;
    const msg =
      reason === "no_amortization"
        ? "El préstamo no tiene cuadro de amortización sincronizado. Ve al préstamo y pulsa 'Rehacer completo'."
        : reason === "ambiguous"
          ? "Varias cuotas del cuadro casan con este movimiento. La selección manual aún no está implementada — usa la vista del préstamo para revisar."
          : "Ninguna cuota del cuadro casa con la fecha e importe del movimiento (±7 días, ±1 €).";
    return (
      <div style={{ marginTop: 12, padding: 10, background: "rgba(80,160,255,0.06)", borderRadius: 6 }}>
        <p style={{ margin: 0 }}>
          <span className="badge warn">Préstamo detectado</span>{" "}
          <strong>{loanLabel}</strong> (contract <code>{preview.chosenLoan.contractNumber}</code>)
        </p>
        <p className="meta" style={{ marginTop: 6 }}>{msg}</p>
      </div>
    );
  }

  const proposed = preview.proposed!;
  const debitTotal = lines.reduce((a, l) => a + (Number.isFinite(l.debit) ? l.debit : 0), 0);
  const creditTotal = lines.reduce((a, l) => a + (Number.isFinite(l.credit) ? l.credit : 0), 0);
  const diff = Math.round((debitTotal - creditTotal) * 100) / 100;
  const balanced = Math.abs(diff) < 0.01;

  const updateLine = (idx: number, patch: Partial<LoanInstallmentLineProposed>) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const submit = async () => {
    if (!balanced) {
      setError(`Asiento descuadrado: Debe=${debitTotal.toFixed(2)} Haber=${creditTotal.toFixed(2)}`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await postLoanInstallmentToSap({
        iban,
        movementUuid,
        loanBankCode: proposed.loanBankCode,
        loanContractNumber: proposed.loanContractNumber,
        lines: lines.map((l) => ({
          accountCode: l.accountCode,
          costingCode: l.costingCode ?? undefined,
          debit: l.debit,
          credit: l.credit,
          memo: l.memo,
        })),
      });
      alert(`✓ Cuota contabilizada. JdtNum ${r.jdtNum}.`);
      await onPosted();
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 12, padding: 12, background: "rgba(80,160,255,0.08)", borderRadius: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span className={preview.detectedBy === "amount" ? "badge warn" : "badge ok"}>
          {preview.detectedBy === "amount" ? "Préstamo detectado (por importe)" : "Préstamo detectado"}
        </span>
        <strong>{loanLabel}</strong>
        <span className="meta">
          contract <code>{preview.chosenLoan.contractNumber}</code>
          {"  ·  "}cuota {proposed.installmentDate}
          {"  ·  "}total {formatMoney(proposed.installmentTotal, currency)}
        </span>
        {preview.detectedBy === "amount" && (
          <span
            className="meta"
            style={{ fontSize: "0.85em", width: "100%" }}
            title="El contractNumber del préstamo NO aparece en la descripción del movimiento. Se identificó por coincidencia de importe+fecha con una única cuota del cuadro. Revisa antes de enviar."
          >
            ⚠ Match por importe — verifica que es la cuota correcta antes de enviar.
          </span>
        )}
        {!expanded && (
          <button type="button" onClick={() => setExpanded(true)} style={{ marginLeft: "auto" }}>
            Contabilizar cuota →
          </button>
        )}
      </div>
      {expanded && (
        <>
          <div className="meta" style={{ marginTop: 8, fontSize: "0.85em" }}>
            Amortizado <strong>{formatMoney(proposed.installmentAmortized, currency)}</strong>
            {"  ·  "}Interés <strong>{formatMoney(proposed.installmentInterest, currency)}</strong>
            {proposed.reclassificationAmount !== null && (
              <>
                {"  ·  "}Reclasif. LP→CP <strong>{formatMoney(proposed.reclassificationAmount, currency)}</strong>
              </>
            )}
            {proposed.suggestedFees > 0 && (
              <>
                {"  ·  "}Comisión sug. <strong>{formatMoney(proposed.suggestedFees, currency)}</strong>
              </>
            )}
          </div>
          <table className="data" style={{ marginTop: 8, fontSize: "0.85em" }}>
            <thead>
              <tr>
                <th>Rol</th>
                <th>Cuenta</th>
                <th>CC</th>
                <th className="num">Debe</th>
                <th className="num">Haber</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td><span className="meta">{ROLE_LABEL[l.role]}</span></td>
                  <td>
                    <input
                      value={l.accountCode}
                      onChange={(e) => updateLine(i, { accountCode: e.target.value })}
                      style={{ width: "7rem" }}
                      disabled={busy}
                    />
                  </td>
                  <td>
                    <input
                      value={l.costingCode ?? ""}
                      onChange={(e) => updateLine(i, { costingCode: e.target.value || null })}
                      style={{ width: "6rem" }}
                      disabled={busy}
                      placeholder={accountRequiresCostingCode(l.accountCode) ? "obligatorio" : ""}
                    />
                  </td>
                  <td className="num">
                    <input
                      type="number"
                      step="0.01"
                      value={l.debit}
                      onChange={(e) => updateLine(i, { debit: Number(e.target.value) || 0 })}
                      style={{ width: "6rem", textAlign: "right" }}
                      disabled={busy}
                    />
                  </td>
                  <td className="num">
                    <input
                      type="number"
                      step="0.01"
                      value={l.credit}
                      onChange={(e) => updateLine(i, { credit: Number(e.target.value) || 0 })}
                      style={{ width: "6rem", textAlign: "right" }}
                      disabled={busy}
                    />
                  </td>
                </tr>
              ))}
              <tr style={{ borderTop: "1px solid rgba(128,128,128,0.4)" }}>
                <td colSpan={3} className="meta" style={{ textAlign: "right" }}>Totales</td>
                <td className="num"><strong>{debitTotal.toFixed(2)}</strong></td>
                <td className="num"><strong>{creditTotal.toFixed(2)}</strong></td>
              </tr>
            </tbody>
          </table>
          {!balanced && (
            <p className="error" style={{ marginTop: 6 }}>
              Descuadrado: diferencia {diff.toFixed(2)} €. Revisa las líneas.
            </p>
          )}
          {error && <p className="error" style={{ marginTop: 6 }}>{error}</p>}
          <div style={{ marginTop: 10, display: "flex", gap: 6, justifyContent: "flex-end" }}>
            <button type="button" onClick={() => setExpanded(false)} disabled={busy}>Cancelar</button>
            <button type="button" onClick={submit} disabled={busy || !balanced}>
              {busy ? "Enviando…" : "Enviar a SAP"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Modal Contabilizar
// -----------------------------------------------------------------------------

function PostingModal({
  iban,
  movementUuid,
  currency,
  companyId,
  onClose,
  onPosted,
}: {
  iban: string;
  movementUuid: string;
  currency: string;
  companyId: string | null;
  onClose: () => void;
  onPosted: () => Promise<void> | void;
}) {
  const [preview, setPreview] = useState<PostingPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [progressMsg, setProgressMsg] = useState<string | null>(null);
  /** Selección MÚLTIPLE de facturas: llaves = `${source}#${docEntry}`. Todas
   *  las seleccionadas deben pertenecer al mismo CardCode y misma source
   *  porque SAP crea UN solo IncomingPayment/VendorPayment por invocación. */
  const [selectedInvoiceKeys, setSelectedInvoiceKeys] = useState<Set<string>>(new Set());
  /** Importe a aplicar por factura seleccionada (key igual que arriba). */
  const [invoiceSumsApplied, setInvoiceSumsApplied] = useState<Map<string, number>>(new Map());
  /** Selección MÚLTIPLE de efectos: el usuario ve la lista completa de
   *  abiertos y marca uno o varios. Al contabilizar se procesa uno a uno. */
  const [selectedBoeKeys, setSelectedBoeKeys] = useState<Set<number>>(new Set());
  /** Filtros locales sobre la lista de efectos abiertos. */
  const [boeFilterText, setBoeFilterText] = useState("");
  const [boeFilterType, setBoeFilterType] = useState<"all" | "receivable" | "payable">("all");
  const [contraOverride, setContraOverride] = useState<string>("");
  /** Pago a cuenta contra un CardCode (sin factura en SAP todavía). */
  const [onAccountCardCode, setOnAccountCardCode] = useState<string>("");
  const [onAccountCardType, setOnAccountCardType] = useState<"customer" | "supplier">("supplier");
  const [costingCodeOverride, setCostingCodeOverride] = useState<string>("");
  const [projectCodeOverride, setProjectCodeOverride] = useState<string>("");
  const [memoOverride, setMemoOverride] = useState<string>("");

  /** Búsqueda manual de interlocutor por nombre para restringir la lista de
   *  candidatos cuando la búsqueda automática no lo ha identificado. */
  const [bpQuery, setBpQuery] = useState<string>("");
  const [bpResults, setBpResults] = useState<BusinessPartnerHit[]>([]);
  const [bpSearchBusy, setBpSearchBusy] = useState<boolean>(false);
  const [bpSearchError, setBpSearchError] = useState<string | null>(null);
  const [bpFilter, setBpFilter] = useState<BusinessPartnerHit | null>(null);
  const [bpCandidates, setBpCandidates] = useState<PostingCandidateInvoice[] | null>(null);
  const [bpLoading, setBpLoading] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const p = await fetchPostingPreview(iban, movementUuid);
        if (cancelled) return;
        setPreview(p);
        if (p.ok) {
          if (p.actionType === "fixed_account" && p.contraAccount) {
            setContraOverride(p.contraAccount);
            if (p.costingCode) setCostingCodeOverride(p.costingCode);
            if (p.projectCode) setProjectCodeOverride(p.projectCode);
          }
          setMemoOverride(p.movement.description?.slice(0, 50) ?? "");
          // Pre-selección de cardType para "pago a cuenta" según signo:
          // cargo → probablemente pago a proveedor; abono → cobro de cliente.
          setOnAccountCardType(p.movement.amount < 0 ? "supplier" : "customer");
          if (p.actionType === "open_items" && p.invoiceCandidates.length > 0) {
            // Pre-selección: el primer candidato del top (mayor score), si
            // matchea por importe exacto — es el caso mayoritario (1 mov =
            // 1 factura). Para agrupaciones el usuario marca las que quiera.
            const first = p.invoiceCandidates[0]!;
            if (first.matchedBy.includes("amount")) {
              const key = invoiceKey(first);
              const initialSum = Math.min(first.openAmount, Math.abs(p.movement.amount));
              setSelectedInvoiceKeys(new Set([key]));
              setInvoiceSumsApplied(new Map([[key, initialSum]]));
            }
          }
          // Preseleccionamos los efectos que casen exactamente por importe
          // — el usuario suele confirmar esos. Si no hay ninguno, deja la
          // lista sin selección para que elija libremente.
          if (p.actionType === "bills_of_exchange" && p.boeCandidates.length > 0) {
            const matchExact = p.boeCandidates.filter((c) => c.matchedBy.includes("amount"));
            setSelectedBoeKeys(new Set(matchExact.map((c) => c.docEntry)));
          }
        }
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [iban, movementUuid]);

  // Búsqueda debounced de interlocutores por nombre. Se salta cuando ya hay
  // un socio seleccionado — el texto del input se mantiene sólo como etiqueta.
  useEffect(() => {
    if (!companyId || bpFilter) return;
    const q = bpQuery.trim();
    if (q.length < 3) {
      setBpResults([]);
      setBpSearchError(null);
      return;
    }
    let cancelled = false;
    setBpSearchBusy(true);
    setBpSearchError(null);
    const t = setTimeout(async () => {
      try {
        const items = await searchBusinessPartnersByName(companyId, q, "any");
        if (!cancelled) setBpResults(items);
      } catch (e) {
        if (!cancelled) setBpSearchError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setBpSearchBusy(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [companyId, bpQuery, bpFilter]);

  // Al seleccionar un BP, trae sus partidas abiertas y restringe la lista.
  const applyBpFilter = async (bp: BusinessPartnerHit) => {
    if (!companyId) return;
    setBpFilter(bp);
    setBpResults([]);
    setBpQuery(bp.cardName);
    setSelectedInvoiceKeys(new Set());
    setInvoiceSumsApplied(new Map());
    setSelectedBoeKeys(new Set());
    setBpLoading(true);
    setBpSearchError(null);
    try {
      const source: "invoices" | "purchase_invoices" | "auto" =
        bp.cardType === "C" ? "invoices" : bp.cardType === "S" ? "purchase_invoices" : "auto";
      const items = await fetchOpenInvoicesByCardCode(companyId, bp.cardCode, source);
      setBpCandidates(items);
      // Autofill del "pago a cuenta" para el caso frecuente sin factura.
      setOnAccountCardCode(bp.cardCode);
      setOnAccountCardType(bp.cardType === "C" ? "customer" : "supplier");
    } catch (e) {
      setBpSearchError(e instanceof Error ? e.message : String(e));
      setBpCandidates([]);
    } finally {
      setBpLoading(false);
    }
  };

  const clearBpFilter = () => {
    setBpFilter(null);
    setBpCandidates(null);
    setBpQuery("");
    setBpResults([]);
    setBpSearchError(null);
  };

  const submit = async () => {
    if (!preview || !preview.ok || busy) return;
    // Si hay N efectos seleccionados, se contabilizan uno a uno. Cada uno
    // aplica el importe total del movimiento (lo habitual: el usuario elige
    // exactamente los que suman lo mismo, o solo uno).
    if (preview.actionType === "bills_of_exchange" && selectedBoeKeys.size > 0) {
      const chosen = preview.boeCandidates.filter((c) => selectedBoeKeys.has(c.docEntry));
      const totalMov = Math.abs(preview.movement.amount);
      // Reparto simple: si N==1 aplica el importe total; si N>1 aplica el
      // importe de cada efecto (SumApplied) — asumimos que la suma cuadra.
      const perItem = chosen.length === 1
        ? [totalMov]
        : chosen.map((c) => c.amount);
      setBusy(true);
      setError(null);
      setProgressMsg(null);
      let done = 0;
      const failures: string[] = [];
      for (let i = 0; i < chosen.length; i++) {
        const boe = chosen[i]!;
        const sumApplied = perItem[i] ?? boe.amount;
        setProgressMsg(`⏳ Contabilizando ${i + 1}/${chosen.length}: efecto ${boe.docNum}…`);
        const boeAccount =
          boe.boeType === "receivable"
            ? preview.boeAccountReceivable ?? ""
            : preview.boeAccountPayable ?? "";
        if (!boeAccount) {
          failures.push(`efecto ${boe.docNum}: sin cuenta ${boe.boeType} en la regla`);
          continue;
        }
        try {
          const r = await postMovementToSap(iban, movementUuid, {
            kind: "boe",
            docEntry: boe.docEntry,
            docNum: boe.docNum,
            boeAccount,
            sumApplied,
            memo: memoOverride || undefined,
          });
          done++;
          const jdtInfo = "async" in r ? "(async)" : r.jdtNum;
          setProgressMsg(`✓ ${done}/${chosen.length} contabilizados. Último JdtNum: ${jdtInfo}`);
        } catch (e: unknown) {
          failures.push(`efecto ${boe.docNum}: ${e instanceof Error ? e.message : String(e)}`);
        }
      }
      setBusy(false);
      if (failures.length === 0) {
        setProgressMsg(`✓ ${done}/${chosen.length} efectos contabilizados correctamente.`);
        await onPosted();
      } else {
        setError(`⚠️ ${done}/${chosen.length} contabilizados. Errores:\n${failures.join("\n")}`);
      }
      return;
    }
    // Leasing installment: propuesta 100% automática. El backend responde 202
    // con un operationId (el flow tarda >60s y el reverse proxy IIS cortaba
    // con 502); el frontend polea el estado cada 2s hasta terminar.
    if (
      preview.actionType === "leasing_installment" &&
      preview.leasingProposal &&
      !preview.leasingProposal.isConstitution
    ) {
      const p = preview.leasingProposal;
      setBusy(true);
      setError(null);
      const leasingKindLabel =
        p.kind === "partial_new"
          ? "PARCIAL (factura + pago parcial)"
          : p.kind === "residual"
            ? "RESIDUO (solo pago que cierra factura)"
            : "cuota completa";
      setProgressMsg(
        `⏳ Contabilizando leasing ${p.contractNumber} cuota ${p.installmentIndex} — ${leasingKindLabel}…`,
      );
      try {
        const r = await postMovementToSap(iban, movementUuid, {
          kind: "leasing_installment",
          bankCode: p.bankCode,
          contractNumber: p.contractNumber,
          installmentIndex: p.installmentIndex,
          installmentKind: p.kind,
          memo: memoOverride || undefined,
        });
        const isAsync = "async" in r && (r as { async?: boolean }).async === true;
        if (isAsync) {
          // Polling cada 2s hasta que status != "running". Timeout tras 5 min.
          const opId = r.operationId;
          const deadline = Date.now() + 5 * 60 * 1000;
          let seconds = 0;
          while (Date.now() < deadline) {
            await new Promise((res) => setTimeout(res, 2000));
            seconds += 2;
            let st: Awaited<ReturnType<typeof getLeasingPostingStatus>>;
            try {
              st = await getLeasingPostingStatus(iban, movementUuid, opId);
            } catch (pollErr: unknown) {
              setProgressMsg(`⏳ (${seconds}s) polling error, reintentando…`);
              continue;
            }
            if (st.status === "ok") {
              setProgressMsg(`✓ Cuota contabilizada. JdtNum ${st.jdtNum} (VendorPayment).`);
              await onPosted();
              return;
            }
            if (st.status === "error") {
              throw new Error(st.message);
            }
            setProgressMsg(
              `⏳ (${seconds}s) Contabilizando en SAP… (factura + pago + reclas.)`,
            );
          }
          throw new Error(
            "Timeout esperando respuesta del backend (5 min). El proceso puede haber terminado en SAP — revisa manualmente.",
          );
        } else {
          const sync = r as import("../api").PostMovementResult;
          setProgressMsg(`✓ Cuota contabilizada. JdtNum ${sync.jdtNum} (VendorPayment).`);
          await onPosted();
        }
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
      return;
    }

    // Cuota de préstamo detectada por regla `loan_installment`: propuesta
    // automática con 3-6 líneas. Se contabiliza con un solo POST a
    // /post-loan-installment (asiento único directo).
    if (preview.actionType === "loan_installment" && preview.loanProposal) {
      const p = preview.loanProposal;
      setBusy(true);
      setError(null);
      const kindLabel =
        p.kind === "partial_new"
          ? "PARCIAL (contra puente)"
          : p.kind === "residual"
            ? "RESIDUO (cierra puente)"
            : "cuota";
      setProgressMsg(
        `⏳ Contabilizando préstamo ${p.loanContractNumber} ${kindLabel}…`,
      );
      try {
        const r = await postLoanInstallmentToSap({
          iban,
          movementUuid,
          loanBankCode: p.loanBankCode,
          loanContractNumber: p.loanContractNumber,
          lines: p.lines.map((l) => ({
            accountCode: l.accountCode,
            costingCode: l.costingCode ?? undefined,
            debit: l.debit,
            credit: l.credit,
            memo: l.memo,
          })),
          memo: memoOverride || undefined,
          kind: p.kind,
          installmentDate: p.installmentDate,
        });
        setProgressMsg(`✓ Cuota contabilizada. JdtNum ${r.jdtNum}.`);
        await onPosted();
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
      return;
    }

    let choice: PostingChoice;
    if (preview.actionType === "open_items" && selectedInvoiceKeys.size > 0) {
      const chosen = preview.invoiceCandidates.filter((c) => selectedInvoiceKeys.has(invoiceKey(c)));
      if (chosen.length === 0) {
        setError("Selecciona al menos una factura o borra la selección.");
        return;
      }
      // Validación: mismo CardCode y misma source (SAP: 1 payment = 1 socio).
      const source = chosen[0]!.source;
      const cardCode = chosen[0]!.cardCode;
      const mismatched = chosen.find((c) => c.source !== source || c.cardCode !== cardCode);
      if (mismatched) {
        setError(
          `No puedes mezclar facturas de distintos socios o fuentes en un mismo pago. ` +
            `Deja solo facturas de ${cardCode} (${source === "invoices" ? "cliente" : "proveedor"}).`,
        );
        return;
      }
      const invoices = chosen.map((c) => ({
        docEntry: c.docEntry,
        sumApplied: invoiceSumsApplied.get(invoiceKey(c)) ?? c.openAmount,
      }));
      const totalApplied = Math.round(invoices.reduce((a, l) => a + l.sumApplied, 0) * 100) / 100;
      const movAbs = Math.round(Math.abs(preview.movement.amount) * 100) / 100;
      if (totalApplied > movAbs + 0.005) {
        setError(
          `Suma aplicada (${formatMoney(totalApplied, currency)}) mayor que el movimiento ` +
            `(${formatMoney(movAbs, currency)}). Reduce alguno.`,
        );
        return;
      }
      choice = { kind: "invoice", source, invoices, memo: memoOverride || undefined };
    } else if (onAccountCardCode.trim()) {
      // Pago a cuenta contra un CardCode (sin factura). El pago quedará
      // pendiente de aplicar en SAP hasta que llegue la factura y el contable
      // lo concilie internamente.
      choice = {
        kind: "on_account",
        cardCode: onAccountCardCode.trim(),
        cardType: onAccountCardType,
        memo: memoOverride || undefined,
      };
    } else {
      const contra = contraOverride.trim();
      if (!contra) {
        setError(
          "Selecciona uno o varios candidatos, indica un CardCode para pago a cuenta, o una cuenta contrapartida fija.",
        );
        return;
      }
      const cc = costingCodeOverride.trim();
      const pj = projectCodeOverride.trim();
      if (accountRequiresCostingCode(contra) && !cc) {
        setError(`La cuenta ${contra} empieza por 6 o 7 — indica un Centro de coste (OPRC).`);
        return;
      }
      if (accountRequiresCostingCode(contra) && !pj) {
        setError(`La cuenta ${contra} empieza por 6 o 7 — indica un Proyecto (OPRJ).`);
        return;
      }
      // Si el preview detectó una comisión de aval concreta (fila del cuadro
      // del aval), enviamos `avalRef` para que el backend marque la fila
      // como pagada tras crear el JournalEntry.
      const avalProposal =
        preview && "avalProposal" in preview ? preview.avalProposal : null;
      const avalRef = avalProposal
        ? {
            bankCode: avalProposal.bankCode,
            lineNumber: avalProposal.lineNumber,
            avalNumber: avalProposal.avalNumber,
            rowType: avalProposal.rowType,
            rowIndex: avalProposal.rowIndex,
          }
        : undefined;
      choice = {
        kind: "fixed_account",
        contraAccount: contra,
        ...(cc ? { costingCode: cc } : {}),
        ...(pj ? { projectCode: pj } : {}),
        memo: memoOverride || undefined,
        ...(avalRef ? { avalRef } : {}),
      };
    }
    setBusy(true);
    setError(null);
    try {
      const r = await postMovementToSap(iban, movementUuid, choice);
      if ("async" in r) {
        // No debería pasar: solo leasing_installment devuelve async, y ese
        // caso está gestionado arriba. Mostramos aviso genérico por defensa.
        alert("✓ Operación iniciada en SAP en segundo plano.");
      } else {
        alert(`✓ Contabilizado. JdtNum ${r.jdtNum} (${r.documentType}).`);
      }
      await onPosted();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{ width: "min(720px, 92vw)", maxHeight: "88vh", overflow: "auto" }}
      >
        <div className="card-head">
          <h3 style={{ margin: 0 }}>Contabilizar movimiento en SAP</h3>
          <button type="button" onClick={onClose} disabled={busy}>
            Cerrar
          </button>
        </div>

        {loading && <p>Buscando regla y candidatos en SAP…</p>}
        {error && <p className="error">{error}</p>}

        {preview && !preview.ok && (
          <div>
            <p className="error">
              {preview.message}
              {preview.reason === "no_matching_rule" && (
                <>
                  {" "}
                  <Link to="/reglas-contabilizacion">Crear regla →</Link>
                </>
              )}
              {preview.reason === "aval_not_detected" && (
                <>
                  {" "}
                  <Link to="/avales">Ir a Avales →</Link>
                </>
              )}
            </p>
            {"leasingResidualReopenCandidates" in preview &&
              preview.leasingResidualReopenCandidates &&
              preview.leasingResidualReopenCandidates.length > 0 && (
                <LeasingResidualReopenSection
                  candidates={preview.leasingResidualReopenCandidates}
                  iban={iban}
                  movementUuid={movementUuid}
                  busy={busy}
                  setBusy={setBusy}
                  setError={setError}
                  currency={currency}
                  onRefreshPreview={async () => {
                    const p = await fetchPostingPreview(iban, movementUuid);
                    setPreview(p);
                  }}
                />
              )}
            {preview.reason === "no_matching_rule" && (
              <LoanInstallmentSection
                iban={iban}
                movementUuid={movementUuid}
                currency={currency}
                onPosted={onPosted}
                onClose={onClose}
              />
            )}
            {preview.reason === "already_posted" && (
              <div style={{ marginTop: 12, padding: 12, background: "rgba(80,160,255,0.06)", borderRadius: 6 }}>
                <p style={{ margin: "0 0 8px 0", fontSize: "0.9em" }}>
                  Si el asiento fue <strong>anulado a mano en SAP</strong> (p.ej. mediante un
                  asiento nuevo con memo "(Anulación)"), la app no lo detecta automáticamente
                  porque en OJDT no queda marca de storno. Pulsa el botón para deshacer la
                  marca de contabilización y poder volver a lanzar el asiento.
                </p>
                <button
                  type="button"
                  disabled={busy}
                  onClick={async () => {
                    if (!confirm("¿Deshacer la marca de 'contabilizado' del movimiento? Podrás volver a lanzar el asiento en SAP.")) return;
                    setBusy(true);
                    setError(null);
                    try {
                      await clearMovementPosting(iban, movementUuid);
                      // Recargar la preview para pintar el modal en modo normal.
                      const p = await fetchPostingPreview(iban, movementUuid);
                      setPreview(p);
                      await onPosted();
                    } catch (e: unknown) {
                      setError(e instanceof Error ? e.message : String(e));
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  🗑 Deshacer marca de contabilización
                </button>
                <RetryReconcileBlock
                  iban={iban}
                  movementUuid={movementUuid}
                  busy={busy}
                  setBusy={setBusy}
                  setError={setError}
                  onDone={onPosted}
                />
              </div>
            )}
          </div>
        )}

        {preview && preview.ok && (
          <>
            <div className="meta" style={{ marginBottom: 12 }}>
              Regla: <strong>{preview.ruleName}</strong>
              {"  ·  "}Acción: <code>{preview.actionType}</code>
              {"  ·  "}G/L banco: <code>{preview.glAccount}</code>
              {"  ·  "}
              <a
                href={`/api/accounts/${encodeURIComponent(iban)}/movements/${encodeURIComponent(movementUuid)}/posting-diagnostics`}
                target="_blank"
                rel="noreferrer"
                title="Ver qué tokens y qué consultas hizo el backend contra SAP"
              >
                Diagnóstico
              </a>
            </div>

            <div style={{ marginBottom: 12 }}>
              <div className="meta">Movimiento</div>
              <div>
                {preview.movement.operationDate} · {" "}
                <strong>
                  {formatMoney(preview.movement.amount, currency)}
                </strong>
              </div>
              <div style={{ fontSize: "0.9em" }}>{preview.movement.description}</div>
              {preview.movement.transferDetails?.senderReceiver && (
                <div className="meta" style={{ fontSize: "0.85em" }}>
                  {preview.movement.transferDetails.senderReceiver}
                </div>
              )}
            </div>

            {preview.actionType === "open_items" && (() => {
              const displayedCandidates: PostingCandidateInvoice[] =
                bpCandidates !== null ? bpCandidates : preview.invoiceCandidates;
              return (
              <div style={{ marginBottom: 12 }}>
                <h4 style={{ margin: "0 0 6px 0" }}>
                  Candidatos SAP ({displayedCandidates.length})
                  {bpFilter && (
                    <span
                      className="badge"
                      style={{
                        marginLeft: 8,
                        fontSize: "0.7em",
                        background: "rgba(80,160,255,0.15)",
                      }}
                    >
                      Filtrado: {bpFilter.cardName}
                    </span>
                  )}
                </h4>
                {!bpFilter && (
                  <p className="meta" style={{ fontSize: "0.85em" }}>
                    Búsqueda automática: importe {formatMoney(preview.searchTokens.amount, currency)}
                    {preview.searchTokens.names.length > 0 && (
                      <> · nombres: {preview.searchTokens.names.map((n) => (
                        <code key={n} style={{ marginRight: 4 }}>{n}</code>
                      ))}</>
                    )}
                    {preview.searchTokens.references.length > 0 && (
                      <> · refs: {preview.searchTokens.references.map((r) => (
                        <code key={r} style={{ marginRight: 4 }}>{r}</code>
                      ))}</>
                    )}
                  </p>
                )}

                {/* Buscador de interlocutor por nombre — filtra la lista de
                    candidatos por CardCode del socio elegido. */}
                {companyId && (
                  <div style={{ margin: "8px 0", position: "relative" }}>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <input
                        type="text"
                        value={bpQuery}
                        onChange={(e) => {
                          setBpQuery(e.target.value);
                          if (bpFilter) {
                            setBpFilter(null);
                            setBpCandidates(null);
                          }
                        }}
                        placeholder="Buscar interlocutor por nombre (≥3 letras)…"
                        style={{ flex: 1, minWidth: "18rem" }}
                      />
                      {bpSearchBusy && <span className="meta">Buscando…</span>}
                      {(bpFilter || bpQuery) && (
                        <button
                          type="button"
                          onClick={clearBpFilter}
                          style={{ fontSize: "0.85em" }}
                          title="Volver a los candidatos automáticos"
                        >
                          Limpiar
                        </button>
                      )}
                    </div>
                    {bpSearchError && (
                      <p className="error" style={{ fontSize: "0.85em", margin: "4px 0 0" }}>
                        {bpSearchError}
                      </p>
                    )}
                    {bpResults.length > 0 && !bpFilter && (
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          background: "var(--card-bg, #fff)",
                          border: "1px solid rgba(0,0,0,0.15)",
                          borderRadius: 6,
                          zIndex: 5,
                          maxHeight: 260,
                          overflow: "auto",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                        }}
                      >
                        {bpResults.map((r) => (
                          <button
                            key={r.cardCode}
                            type="button"
                            onClick={() => applyBpFilter(r)}
                            style={{
                              display: "block",
                              width: "100%",
                              textAlign: "left",
                              padding: "6px 10px",
                              background: "transparent",
                              border: "none",
                              borderBottom: "1px solid rgba(0,0,0,0.06)",
                              cursor: "pointer",
                            }}
                          >
                            <div style={{ fontSize: "0.9em" }}>
                              <code>{r.cardCode}</code>{" "}
                              <span className="meta" style={{ fontSize: "0.85em" }}>
                                ({r.cardType === "C" ? "Cliente" : r.cardType === "S" ? "Proveedor" : r.cardType})
                              </span>
                            </div>
                            <div>{r.cardName}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {bpLoading ? (
                  <p className="meta">Cargando partidas abiertas del socio…</p>
                ) : displayedCandidates.length === 0 ? (
                  <p className="meta">
                    {bpFilter
                      ? `${bpFilter.cardName} no tiene facturas abiertas en SAP. Usa "Pago a cuenta" o la contrapartida de fallback.`
                      : "Ninguna factura abierta encontrada. Usa la contrapartida de fallback abajo."}
                  </p>
                ) : (
                  <>
                    {(() => {
                      const movAbs = Math.abs(preview.movement.amount);
                      const totalApplied =
                        Math.round(
                          Array.from(selectedInvoiceKeys).reduce(
                            (a, k) => a + (invoiceSumsApplied.get(k) ?? 0),
                            0,
                          ) * 100,
                        ) / 100;
                      const diff = Math.round((movAbs - totalApplied) * 100) / 100;
                      const overflow = totalApplied - movAbs > 0.005;
                      return (
                        <p
                          className="meta"
                          style={{
                            fontSize: "0.85em",
                            marginTop: 4,
                            color: overflow ? "#b91c1c" : undefined,
                          }}
                        >
                          Aplicado <strong>{formatMoney(totalApplied, currency)}</strong> /
                          {" "}Movimiento <strong>{formatMoney(movAbs, currency)}</strong>
                          {" · "}Diferencia <strong>{formatMoney(diff, currency)}</strong>
                          {selectedInvoiceKeys.size > 1 && (
                            <> · <span className="meta">Marcadas {selectedInvoiceKeys.size} facturas</span></>
                          )}
                        </p>
                      );
                    })()}
                  <table className="data">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Fuente</th>
                        <th>DocNum</th>
                        <th>Fecha</th>
                        <th>CardCode</th>
                        <th className="num">Pendiente</th>
                        <th className="num">Aplicar</th>
                        <th>Matchea por</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedCandidates.map((c) => {
                        const key = invoiceKey(c);
                        const isSelected = selectedInvoiceKeys.has(key);
                        // Toggle: agregar sólo si es compatible (misma source + CardCode) con las ya elegidas.
                        const toggle = () => {
                          setSelectedInvoiceKeys((prev) => {
                            const next = new Set(prev);
                            if (next.has(key)) {
                              next.delete(key);
                              setInvoiceSumsApplied((prevSums) => {
                                const nextSums = new Map(prevSums);
                                nextSums.delete(key);
                                return nextSums;
                              });
                              return next;
                            }
                            const others = displayedCandidates.filter((x) =>
                              prev.has(invoiceKey(x)),
                            );
                            const other = others[0];
                            if (other && (other.source !== c.source || other.cardCode !== c.cardCode)) {
                              alert(
                                `Solo puedes seleccionar facturas del mismo socio y misma fuente. Ya has marcado ${other.cardCode} (${other.source === "invoices" ? "cliente" : "proveedor"}).`,
                              );
                              return prev;
                            }
                            next.add(key);
                            setSelectedBoeKeys(new Set());
                            // Importe inicial: openAmount, capado al restante del movimiento
                            const alreadyApplied = Array.from(prev).reduce(
                              (a, k) => a + (invoiceSumsApplied.get(k) ?? 0),
                              0,
                            );
                            const remaining =
                              Math.round((Math.abs(preview.movement.amount) - alreadyApplied) * 100) / 100;
                            const initial = Math.max(0, Math.min(c.openAmount, remaining));
                            setInvoiceSumsApplied((prevSums) => {
                              const nextSums = new Map(prevSums);
                              nextSums.set(key, initial);
                              return nextSums;
                            });
                            return next;
                          });
                        };
                        return (
                        <tr
                          key={key}
                          onClick={toggle}
                          style={{
                            cursor: "pointer",
                            background: isSelected ? "rgba(80,160,255,0.15)" : undefined,
                          }}
                        >
                          <td onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={toggle}
                            />
                          </td>
                          <td>
                            {c.source === "invoices" ? "Cliente" : "Proveedor"}
                          </td>
                          <td>
                            <code>{c.docNum}</code>
                            {c.numAtCard && (
                              <div className="meta" style={{ fontSize: "0.8em" }}>
                                ref: {c.numAtCard}
                              </div>
                            )}
                          </td>
                          <td>{c.docDate}</td>
                          <td>
                            <div style={{ fontSize: "0.9em" }}>
                              <code>{c.cardCode}</code>
                              {c.cardName && <div className="meta">{c.cardName}</div>}
                              <div style={{ marginTop: 4 }}>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOnAccountCardCode(c.cardCode);
                                    setOnAccountCardType(
                                      c.source === "invoices" ? "customer" : "supplier",
                                    );
                                    setSelectedInvoiceKeys(new Set());
                                    setInvoiceSumsApplied(new Map());
                                    setSelectedBoeKeys(new Set());
                                  }}
                                  style={{ fontSize: "0.7em", padding: "1px 6px" }}
                                  title="Contabilizar como pago a cuenta contra este proveedor (fecha del movimiento)"
                                >
                                  Pagar a cuenta
                                </button>
                              </div>
                            </div>
                          </td>
                          <td className="num">{formatMoney(c.openAmount, c.currency || currency)}</td>
                          <td className="num" onClick={(e) => e.stopPropagation()}>
                            {isSelected ? (
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={invoiceSumsApplied.get(key) ?? 0}
                                onChange={(e) => {
                                  const v = Number(e.target.value);
                                  setInvoiceSumsApplied((prev) => {
                                    const next = new Map(prev);
                                    next.set(key, Number.isFinite(v) ? v : 0);
                                    return next;
                                  });
                                }}
                                style={{ width: 100, textAlign: "right" }}
                              />
                            ) : (
                              <span className="meta">—</span>
                            )}
                          </td>
                          <td>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                              {c.matchedBy.map((m) => (
                                <span
                                  key={m}
                                  className="badge ok"
                                  style={{ fontSize: "0.7em", padding: "0.1rem 0.35rem" }}
                                  title={`Score total: ${c.score}`}
                                >
                                  {m === "amount" ? "€" : m === "name" ? "nombre" : "ref"}
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </>
                )}
              </div>
              );
            })()}

            {preview.actionType === "leasing_installment" && preview.leasingProposal && (
              <LeasingInstallmentPreview
                proposal={preview.leasingProposal}
                currency={currency}
              />
            )}

            {preview.actionType === "loan_installment" && preview.loanProposal && (
              <LoanInstallmentAutoPreview
                proposal={preview.loanProposal}
                currency={currency}
              />
            )}

            {preview.actionType === "bills_of_exchange" && (() => {
              const all = preview.boeCandidates;
              const needle = boeFilterText.trim().toLowerCase();
              const filtered = all.filter((c) => {
                if (boeFilterType === "receivable" && c.boeType !== "receivable") return false;
                if (boeFilterType === "payable" && c.boeType !== "payable") return false;
                if (needle) {
                  const hay =
                    `${c.docNum} ${c.cardCode} ${c.cardName} ${c.reference1} ${c.reference2}`.toLowerCase();
                  if (!hay.includes(needle)) return false;
                }
                return true;
              });
              const chosen = all.filter((c) => selectedBoeKeys.has(c.docEntry));
              const sumChosen = chosen.reduce((a, c) => a + c.amount, 0);
              const movAbs = Math.abs(preview.movement.amount);
              const diff = Math.round((sumChosen - movAbs) * 100) / 100;
              const cuadra = chosen.length > 0 && diff === 0;
              return (
                <div style={{ marginBottom: 12 }}>
                  <h4 style={{ margin: "0 0 6px 0" }}>
                    Efectos abiertos en SAP ({all.length})
                  </h4>
                  <p className="meta" style={{ fontSize: "0.85em" }}>
                    Elige uno o varios. Movimiento: <strong>{formatMoney(movAbs, currency)}</strong> · Preseleccionados los que casen exactamente por importe.
                  </p>
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                      flexWrap: "wrap",
                      marginBottom: 8,
                    }}
                  >
                    <select
                      value={boeFilterType}
                      onChange={(e) =>
                        setBoeFilterType(e.target.value as "all" | "receivable" | "payable")
                      }
                    >
                      <option value="all">Todos</option>
                      <option value="receivable">A cobrar (cliente)</option>
                      <option value="payable">A pagar (proveedor)</option>
                    </select>
                    <input
                      type="text"
                      placeholder="Buscar por nº, código o nombre…"
                      value={boeFilterText}
                      onChange={(e) => setBoeFilterText(e.target.value)}
                      style={{ minWidth: "16rem" }}
                    />
                    <span className="meta">Mostrando {filtered.length} de {all.length}</span>
                    {chosen.length > 0 && (
                      <span
                        className={cuadra ? "badge ok" : "badge warn"}
                        title="Suma de los seleccionados vs. importe del movimiento"
                      >
                        Sel {chosen.length} · Σ {formatMoney(sumChosen, currency)} · Δ {formatMoney(diff, currency)}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => setSelectedBoeKeys(new Set())}
                      disabled={selectedBoeKeys.size === 0 || busy}
                      style={{ fontSize: "0.85em" }}
                    >
                      Limpiar selección
                    </button>
                  </div>
                  {all.length === 0 ? (
                    <p className="meta">
                      No hay efectos abiertos en SAP. Usa la contrapartida de fallback abajo.
                    </p>
                  ) : (
                    <table className="data" style={{ maxHeight: 340, display: "block", overflow: "auto" }}>
                      <thead>
                        <tr>
                          <th></th>
                          <th>Tipo</th>
                          <th>DocNum</th>
                          <th>Vencimiento</th>
                          <th>Librador / Librado</th>
                          <th className="num">Importe</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((c) => {
                          const isSelected = selectedBoeKeys.has(c.docEntry);
                          const matchesAmount = c.matchedBy.includes("amount");
                          const toggle = () => {
                            setSelectedInvoiceKeys(new Set());
                            setInvoiceSumsApplied(new Map());
                            setSelectedBoeKeys((prev) => {
                              const next = new Set(prev);
                              if (next.has(c.docEntry)) next.delete(c.docEntry);
                              else next.add(c.docEntry);
                              return next;
                            });
                          };
                          return (
                            <tr
                              key={c.docEntry}
                              onClick={toggle}
                              style={{
                                cursor: "pointer",
                                background: isSelected
                                  ? "rgba(80,160,255,0.18)"
                                  : matchesAmount
                                    ? "rgba(60,180,120,0.08)"
                                    : undefined,
                              }}
                            >
                              <td>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={toggle}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </td>
                              <td>
                                {c.boeType === "payable"
                                  ? "A pagar"
                                  : c.boeType === "receivable"
                                    ? "A cobrar"
                                    : "?"}
                              </td>
                              <td>
                                <code>{c.docNum}</code>
                                {(c.reference1 || c.reference2) && (
                                  <div className="meta" style={{ fontSize: "0.8em" }}>
                                    {[c.reference1, c.reference2].filter(Boolean).join(" · ")}
                                  </div>
                                )}
                              </td>
                              <td>{c.docDueDate}</td>
                              <td>
                                <div style={{ fontSize: "0.9em" }}>
                                  <code>{c.cardCode}</code>
                                  {c.cardName && <div className="meta">{c.cardName}</div>}
                                </div>
                              </td>
                              <td className="num">{formatMoney(c.amount, c.currency || currency)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })()}

            {/* Pago a cuenta — sólo cuando la regla es open_items y no se ha marcado nada.
                Útil cuando la factura no está aún en SAP: se crea un pago contra el
                CardCode y queda pendiente hasta que llegue la factura. */}
            {preview.actionType === "open_items" && (
              <div style={{ marginBottom: 12 }}>
                <h4 style={{ margin: "0 0 6px 0" }}>…o pago a cuenta (sin factura)</h4>
                <p className="meta" style={{ fontSize: "0.85em" }}>
                  Se contabiliza como IncomingPayment/VendorPayment contra el CardCode
                  indicado, sin aplicar a factura. Queda pendiente en SAP para conciliar
                  cuando llegue la factura.
                </p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "end" }}>
                  <label>
                    <div className="meta">CardCode del socio</div>
                    <input
                      type="text"
                      value={onAccountCardCode}
                      onChange={(e) => {
                        setOnAccountCardCode(e.target.value);
                        if (e.target.value.trim()) {
                          setSelectedInvoiceKeys(new Set());
                          setInvoiceSumsApplied(new Map());
                          setSelectedBoeKeys(new Set());
                        }
                      }}
                      placeholder="P0042"
                      style={{ width: 180 }}
                    />
                  </label>
                  <label>
                    <div className="meta">Tipo</div>
                    <select
                      value={onAccountCardType}
                      onChange={(e) => setOnAccountCardType(e.target.value as "customer" | "supplier")}
                    >
                      <option value="supplier">Proveedor</option>
                      <option value="customer">Cliente</option>
                    </select>
                  </label>
                </div>
              </div>
            )}

            {/* Banner: comisión de AVAL detectada — la regla es fixed_account
                pero el preview identificó un aval concreto y sustituyó
                cuenta/CC/PJ con los del aval. */}
            {preview.actionType === "fixed_account" && preview.avalProposal && (
              <div
                style={{
                  background: "rgba(30, 130, 76, 0.08)",
                  border: "1px solid rgba(30, 130, 76, 0.35)",
                  borderRadius: 6,
                  padding: "8px 10px",
                  marginBottom: 12,
                  fontSize: "0.9em",
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  ✔ Comisión de aval detectada
                </div>
                <div className="meta">
                  <strong>{preview.avalProposal.avalRecipient}</strong> · Aval nº{" "}
                  {preview.avalProposal.avalNumber} · Línea{" "}
                  {preview.avalProposal.lineAlias || preview.avalProposal.lineNumber}
                </div>
                <div className="meta">
                  Cuadro fila{" "}
                  {preview.avalProposal.rowType === "opening"
                    ? "APERTURA"
                    : `PERIÓDICA #${preview.avalProposal.rowIndex}`}{" "}
                  · vto {preview.avalProposal.rowDueDate.slice(0, 10)} · comisión{" "}
                  {preview.avalProposal.rowCommission.toLocaleString("es-ES", {
                    style: "currency",
                    currency: "EUR",
                    minimumFractionDigits: 2,
                  })}
                </div>
                <div className="meta" style={{ marginTop: 4 }}>
                  Se contabilizará con: cuenta{" "}
                  <code>{preview.avalProposal.expenseAccount || "—"}</code>
                  {preview.avalProposal.costingCode && (
                    <> · CECO <code>{preview.avalProposal.costingCode}</code></>
                  )}
                  {preview.avalProposal.projectCode && (
                    <> · proyecto <code>{preview.avalProposal.projectCode}</code></>
                  )}
                </div>
                {!preview.avalProposal.expenseAccount && (
                  <div className="error" style={{ marginTop: 4 }}>
                    ⚠️ La línea de aval no tiene "Cta. gasto SAP" configurada — no
                    se podrá contabilizar hasta que la fijes en Líneas de Avales.
                  </div>
                )}
                <div className="meta" style={{ marginTop: 4, fontStyle: "italic" }}>
                  Al confirmar, se marcará la fila del cuadro como pagada
                  automáticamente.
                </div>
              </div>
            )}

            {/* Fallback contrapartida fija — siempre visible como escape hatch */}
            <div style={{ marginBottom: 12 }}>
              <h4 style={{ margin: "0 0 6px 0" }}>
                {preview.actionType === "fixed_account"
                  ? "Contrapartida (regla)"
                  : "…o contrapartida fija"}
              </h4>
              <label>
                <div className="meta">Cuenta contable SAP</div>
                <input
                  type="text"
                  value={contraOverride}
                  onChange={(e) => {
                    setContraOverride(e.target.value);
                    if (e.target.value.trim()) {
                      setSelectedInvoiceKeys(new Set());
                            setInvoiceSumsApplied(new Map());
                      setSelectedBoeKeys(new Set());
                    }
                  }}
                  placeholder="626000000000"
                  style={{ width: "100%" }}
                />
              </label>
              {(() => {
                const requiresCC = accountRequiresCostingCode(contraOverride);
                const ccEmpty = !costingCodeOverride.trim();
                const pjEmpty = !projectCodeOverride.trim();
                if (!requiresCC && !costingCodeOverride && !projectCodeOverride) return null;
                return (
                  <>
                    <label style={{ marginTop: 6, display: "block" }}>
                      <div className="meta">
                        Centro de coste (OPRC)
                        {requiresCC && (
                          <span
                            style={{ color: ccEmpty ? "#c62828" : "#2e7d32", marginLeft: 4 }}
                            title="Obligatorio cuando la cuenta empieza por 6 o 7"
                          >
                            {ccEmpty ? "· obligatorio" : "✓"}
                          </span>
                        )}
                      </div>
                      <input
                        type="text"
                        value={costingCodeOverride}
                        onChange={(e) => setCostingCodeOverride(e.target.value)}
                        placeholder={requiresCC ? "obligatorio para cuentas 6* / 7*" : "opcional"}
                        style={{
                          width: "100%",
                          ...(requiresCC && ccEmpty ? { borderColor: "#c62828" } : {}),
                        }}
                      />
                    </label>
                    <label style={{ marginTop: 6, display: "block" }}>
                      <div className="meta">
                        Proyecto (OPRJ)
                        {requiresCC && (
                          <span
                            style={{ color: pjEmpty ? "#c62828" : "#2e7d32", marginLeft: 4 }}
                            title="Obligatorio cuando la cuenta empieza por 6 o 7"
                          >
                            {pjEmpty ? "· obligatorio" : "✓"}
                          </span>
                        )}
                      </div>
                      <input
                        type="text"
                        value={projectCodeOverride}
                        onChange={(e) => setProjectCodeOverride(e.target.value)}
                        placeholder={requiresCC ? "obligatorio para cuentas 6* / 7*" : "opcional"}
                        style={{
                          width: "100%",
                          ...(requiresCC && pjEmpty ? { borderColor: "#c62828" } : {}),
                        }}
                      />
                    </label>
                  </>
                );
              })()}
            </div>

            <div style={{ marginBottom: 12 }}>
              <label>
                <div className="meta">Memo (máx 50)</div>
                <input
                  type="text"
                  value={memoOverride}
                  maxLength={50}
                  onChange={(e) => setMemoOverride(e.target.value)}
                  style={{ width: "100%" }}
                />
              </label>
            </div>

            {progressMsg && (
              <p
                className={progressMsg.startsWith("⚠️") ? "error" : "meta"}
                style={{ whiteSpace: "pre-wrap" }}
              >
                {progressMsg}
              </p>
            )}

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button type="button" onClick={onClose} disabled={busy}>
                Cancelar
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={
                  busy ||
                  (preview.actionType !== "leasing_installment" &&
                    preview.actionType !== "loan_installment" &&
                    selectedInvoiceKeys.size === 0 &&
                    selectedBoeKeys.size === 0 &&
                    !onAccountCardCode.trim() &&
                    !contraOverride.trim()) ||
                  (preview.actionType === "leasing_installment" &&
                    (!preview.leasingProposal || preview.leasingProposal.isConstitution)) ||
                  (preview.actionType === "loan_installment" && !preview.loanProposal)
                }
              >
                {busy
                  ? "Contabilizando…"
                  : selectedBoeKeys.size > 1
                    ? `Contabilizar ${selectedBoeKeys.size} efectos`
                    : "Contabilizar en SAP"}
              </button>
            </div>
          </>
        )}

        <MarkPostedManualSection
          iban={iban}
          movementUuid={movementUuid}
          onPosted={onPosted}
          onClose={onClose}
        />
      </div>
    </div>
  );
}

/** Preview del asiento de una cuota de leasing detectada automáticamente. */
function LeasingInstallmentPreview({
  proposal,
  currency,
}: {
  proposal: import("../api").PostingLeasingProposal;
  currency: string;
}) {
  if (proposal.isConstitution) {
    return (
      <div style={{ marginBottom: 12 }}>
        <h4 style={{ margin: "0 0 6px 0" }}>Leasing detectado — constitución/apertura</h4>
        <p className="error">
          Este movimiento parece ser la <strong>constitución</strong> del leasing
          {" "}<code>{proposal.contractNumber}</code> ({proposal.leasingAlias || "sin alias"}).
          El asiento de constitución aún no está automatizado. Contabilízalo a mano
          en SAP y márcalo como pagado con el botón "Marcar" del cuadro.
        </p>
      </div>
    );
  }
  const linesSum = proposal.invoiceLines.reduce((a, l) => a + l.lineTotal, 0);
  const kindBadge =
    proposal.kind === "partial_new" ? (
      <span
        style={{
          background: "rgba(255,165,0,0.18)",
          border: "1px solid rgba(255,140,0,0.5)",
          borderRadius: 4,
          padding: "1px 6px",
          fontSize: "0.8em",
          marginLeft: 6,
        }}
        title="El banco cargó menos que la cuota completa. Se crea la factura entera y un pago PARCIAL — deja saldo pendiente en el acreedor."
      >
        PAGO PARCIAL
      </span>
    ) : proposal.kind === "residual" ? (
      <span
        style={{
          background: "rgba(80,200,120,0.18)",
          border: "1px solid rgba(60,160,90,0.5)",
          borderRadius: 4,
          padding: "1px 6px",
          fontSize: "0.8em",
          marginLeft: 6,
        }}
        title="Cargo residual que cierra una cuota partial. Solo se emite un pago adicional contra la factura ya existente."
      >
        RESIDUO — CIERRA CUOTA
      </span>
    ) : proposal.kind === "partial_add" ? (
      <span
        style={{
          background: "rgba(255,165,0,0.18)",
          border: "1px solid rgba(255,140,0,0.5)",
          borderRadius: 4,
          padding: "1px 6px",
          fontSize: "0.8em",
          marginLeft: 6,
        }}
        title="Otro cargo parcial sobre la misma cuota (aún no la cierra). Se reutiliza la factura ya emitida — no se crea una nueva ni se hace reclas."
      >
        PARCIAL ADICIONAL
      </span>
    ) : null;
  return (
    <div style={{ marginBottom: 12 }}>
      <h4 style={{ margin: "0 0 6px 0" }}>
        Leasing detectado — cuota mensual
        {kindBadge}
      </h4>
      {(proposal.kind === "residual" || proposal.kind === "partial_add") && (
        <p className="meta" style={{ fontSize: "0.85em", color: "var(--warn, #c60)" }}>
          Ya pagado antes contra factura DocEntry{" "}
          <code>{proposal.invoiceDocEntry ?? "?"}</code>:{" "}
          <strong>{formatMoney(proposal.paidBefore, currency)}</strong>.{" "}
          {proposal.kind === "residual"
            ? "Este pago liquida el saldo pendiente y cierra la cuota."
            : "Este pago es un parcial adicional; la cuota seguirá abierta esperando el residuo."}
          {" "}NO se emite factura nueva.
        </p>
      )}
      <p className="meta" style={{ fontSize: "0.85em" }}>
        Contrato <code>{proposal.contractNumber}</code>
        {proposal.leasingAlias && <> · {proposal.leasingAlias}</>}
        {" · "}Proveedor <code>{proposal.supplierCardCode}</code>
        {proposal.supplierName && <> ({proposal.supplierName})</>}
        {" · "}Cuota <strong>{proposal.installmentIndex}</strong> del {proposal.installmentDate}
      </p>
      {proposal.kind !== "residual" && proposal.kind !== "partial_add" && (
        <>
          <h5 style={{ margin: "8px 0 4px 0", fontSize: "0.9em" }}>
            Factura de proveedor (líneas contables)
          </h5>
          <table className="data">
            <thead>
              <tr>
                <th>Concepto</th>
                <th>Cuenta</th>
                <th>Centro de coste</th>
                <th>Proyecto</th>
                <th>VAT</th>
                <th className="num">Importe</th>
              </tr>
            </thead>
            <tbody>
              {proposal.invoiceLines.map((l) => (
                <tr key={l.role}>
                  <td>{l.description}</td>
                  <td><code>{l.accountCode}</code></td>
                  <td>{l.costingCode ? <code>{l.costingCode}</code> : <span className="meta">—</span>}</td>
                  <td>{l.projectCode ? <code>{l.projectCode}</code> : <span className="meta">—</span>}</td>
                  <td>{l.vatGroup ? <code>{l.vatGroup}</code> : <span className="meta">auto</span>}</td>
                  <td className="num">{formatMoney(l.lineTotal, currency)}</td>
                </tr>
              ))}
              <tr style={{ fontWeight: 600, borderTop: "1px solid rgba(128,128,128,0.4)" }}>
                <td colSpan={5}>Subtotal (sin IVA)</td>
                <td className="num">{formatMoney(linesSum, currency)}</td>
              </tr>
            </tbody>
          </table>
        </>
      )}
      {proposal.reclass && (
        <>
          <h5 style={{ margin: "8px 0 4px 0", fontSize: "0.9em" }}>
            Reclasificación LP → CP (cuota N+12)
          </h5>
          <table className="data">
            <thead>
              <tr>
                <th>Concepto</th>
                <th>Cuenta</th>
                <th className="num">Debe</th>
                <th className="num">Haber</th>
              </tr>
            </thead>
            <tbody>
              {proposal.reclass.lines.map((l) => (
                <tr key={l.role}>
                  <td>{l.memo}</td>
                  <td><code>{l.accountCode}</code></td>
                  <td className="num">{l.debit > 0 ? formatMoney(l.debit, currency) : <span className="meta">—</span>}</td>
                  <td className="num">{l.credit > 0 ? formatMoney(l.credit, currency) : <span className="meta">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      <p className="meta" style={{ fontSize: "0.8em", marginTop: 8 }}>
        Al pulsar "Contabilizar en SAP" se ejecutan en cadena: POST PurchaseInvoice
        → POST VendorPayment aplicando la factura al banco → POST JournalEntry
        de reclasificación (si aplica). La cuota se marcará como pagada.
      </p>
    </div>
  );
}

/** Preview del asiento de cuota de préstamo detectada automáticamente. */
function LoanInstallmentAutoPreview({
  proposal,
  currency,
}: {
  proposal: import("../api").PostingLoanProposal;
  currency: string;
}) {
  const kindBadge =
    proposal.kind === "partial_new" ? (
      <span
        style={{
          background: "rgba(255,165,0,0.18)",
          border: "1px solid rgba(255,140,0,0.5)",
          borderRadius: 4,
          padding: "1px 6px",
          fontSize: "0.8em",
          marginLeft: 6,
        }}
        title="El banco cargó menos que la cuota total. Se contabiliza contra la cuenta puente hasta que llegue el resto."
      >
        PAGO PARCIAL
      </span>
    ) : proposal.kind === "residual" ? (
      <span
        style={{
          background: "rgba(80,200,120,0.18)",
          border: "1px solid rgba(60,160,90,0.5)",
          borderRadius: 4,
          padding: "1px 6px",
          fontSize: "0.8em",
          marginLeft: 6,
        }}
        title="Cargo residual que cierra una cuota parcial. Cancela la cuenta puente y genera el asiento completo."
      >
        RESIDUO — CIERRA CUOTA
      </span>
    ) : null;
  return (
    <div style={{ marginBottom: 12 }}>
      <h4 style={{ margin: "0 0 6px 0" }}>
        Préstamo detectado — cuota mensual
        {kindBadge}
      </h4>
      {proposal.kind === "residual" && proposal.paidBefore > 0 && (
        <p className="meta" style={{ fontSize: "0.85em", color: "var(--warn, #c60)" }}>
          Ya pagado antes contra puente:{" "}
          <strong>{formatMoney(proposal.paidBefore, currency)}</strong>. Este
          asiento cancela la puente y cierra la cuota.
        </p>
      )}
      <p className="meta" style={{ fontSize: "0.85em" }}>
        Contrato <code>{proposal.loanContractNumber}</code>
        {proposal.loanAlias && <> · {proposal.loanAlias}</>}
        {" · "}Cuota <strong>{proposal.installmentIndex + 1}</strong> del {proposal.installmentDate}
        {" · "}Amortiz. <strong>{formatMoney(proposal.installmentAmortized, currency)}</strong>
        {" · "}Interés <strong>{formatMoney(proposal.installmentInterest, currency)}</strong>
        {proposal.suggestedFees > 0 && (
          <> · Comisiones <strong>{formatMoney(proposal.suggestedFees, currency)}</strong></>
        )}
      </p>
      <table className="data">
        <thead>
          <tr>
            <th>Rol</th>
            <th>Concepto</th>
            <th>Cuenta</th>
            <th>Centro de coste</th>
            <th className="num">Debe</th>
            <th className="num">Haber</th>
          </tr>
        </thead>
        <tbody>
          {proposal.lines.map((l, i) => (
            <tr key={i}>
              <td><code>{l.role}</code></td>
              <td>{l.memo}</td>
              <td><code>{l.accountCode}</code></td>
              <td>{l.costingCode ? <code>{l.costingCode}</code> : <span className="meta">—</span>}</td>
              <td className="num">{l.debit > 0 ? formatMoney(l.debit, currency) : <span className="meta">—</span>}</td>
              <td className="num">{l.credit > 0 ? formatMoney(l.credit, currency) : <span className="meta">—</span>}</td>
            </tr>
          ))}
          <tr style={{ fontWeight: 600, borderTop: "1px solid rgba(128,128,128,0.4)" }}>
            <td colSpan={4}>Totales</td>
            <td className="num">{formatMoney(proposal.debitTotal, currency)}</td>
            <td className="num">{formatMoney(proposal.creditTotal, currency)}</td>
          </tr>
        </tbody>
      </table>
      <p className="meta" style={{ fontSize: "0.8em", marginTop: 8 }}>
        {proposal.kind === "partial_new" ? (
          <>Se envía un JournalEntry con 2 líneas: Debe cuenta puente / Haber banco por el importe del cargo. La cuota queda marcada como PARCIAL hasta que llegue el resto.</>
        ) : proposal.kind === "residual" ? (
          <>Se envía el JournalEntry completo cerrando la cuota: Debe amortización CP + intereses{proposal.suggestedFees > 0 && " + comisiones"} / Haber banco (residuo) + Haber puente (cancela lo pagado antes){proposal.reclassificationAmount ? " + reclasificación LP→CP de la cuota N+12" : ""}.</>
        ) : (
          <>Al pulsar "Contabilizar en SAP" se envía un único JournalEntry con las líneas de arriba (amortización a CP, intereses, {proposal.suggestedFees > 0 && "comisiones, "}banco{proposal.reclassificationAmount ? ", y reclasificación LP→CP de la cuota N+12" : ""}).</>
        )}
      </p>
    </div>
  );
}

/**
 * Bloque para reintentar la reconciliación interna en SAP cuando el pago
 * ya se creó (fase 1 OK) pero el Reconcile (fase 2) falló. Aparece dentro
 * del bloque `already_posted` y pide al usuario los datos mínimos.
 */
function RetryReconcileBlock({
  iban,
  movementUuid,
  busy,
  setBusy,
  setError,
  onDone,
}: {
  iban: string;
  movementUuid: string;
  busy: boolean;
  setBusy: (b: boolean) => void;
  setError: (s: string | null) => void;
  onDone: () => Promise<void> | void;
}) {
  const [open, setOpen] = useState(false);
  const [cardCode, setCardCode] = useState("");
  const [source, setSource] = useState<"purchase_invoices" | "invoices">("purchase_invoices");
  const [docEntry, setDocEntry] = useState("");
  const [amount, setAmount] = useState("");
  const [ok, setOk] = useState<string | null>(null);

  const submit = async () => {
    const de = Number(docEntry.trim());
    const amt = Number(amount.trim().replace(",", "."));
    if (!cardCode.trim()) return alert("Rellena CardCode");
    if (!Number.isFinite(de) || de <= 0) return alert("DocEntry inválido");
    if (!Number.isFinite(amt) || amt <= 0) return alert("Importe inválido");
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      const r = await import("../api").then((m) =>
        m.retryInternalReconciliation(iban, movementUuid, {
          cardCode: cardCode.trim(),
          source,
          invoices: [{ docEntry: de, amount: amt }],
        }),
      );
      setOk(`✓ Reconciliación interna OK (JdtNum ${r.jdtNum}, ${r.reconciledRows} líneas).`);
      await onDone();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 12 }}>
      <button type="button" onClick={() => setOpen((x) => !x)} disabled={busy}>
        🔁 {open ? "Cancelar reintento" : "Reintentar reconciliación interna en SAP"}
      </button>
      {open && (
        <div style={{ marginTop: 8, padding: 10, background: "rgba(120,150,200,0.06)", borderRadius: 6 }}>
          <p style={{ margin: "0 0 8px 0", fontSize: "0.85em" }}>
            Usa esto cuando el pago se creó bien pero SAP rechazó unirlo a la factura.
            Introduce el CardCode del proveedor/cliente y el DocEntry+importe de la
            factura a saldar.
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <label style={{ fontSize: "0.85em" }}>
              CardCode<br />
              <input value={cardCode} onChange={(e) => setCardCode(e.target.value)} style={{ width: 120 }} placeholder="P03096" />
            </label>
            <label style={{ fontSize: "0.85em" }}>
              Fuente<br />
              <select value={source} onChange={(e) => setSource(e.target.value as typeof source)}>
                <option value="purchase_invoices">Factura proveedor</option>
                <option value="invoices">Factura cliente</option>
              </select>
            </label>
            <label style={{ fontSize: "0.85em" }}>
              DocEntry<br />
              <input value={docEntry} onChange={(e) => setDocEntry(e.target.value)} style={{ width: 100 }} placeholder="307" />
            </label>
            <label style={{ fontSize: "0.85em" }}>
              Importe (€)<br />
              <input value={amount} onChange={(e) => setAmount(e.target.value)} style={{ width: 100 }} placeholder="7114.67" />
            </label>
          </div>
          <div style={{ marginTop: 8 }}>
            <button type="button" onClick={submit} disabled={busy}>
              {busy ? "Reintentando…" : "Reconciliar en SAP"}
            </button>
          </div>
          {ok && <p style={{ color: "#2e7d32", marginTop: 6, fontSize: "0.9em" }}>{ok}</p>}
        </div>
      )}
    </div>
  );
}

/** Bloque que aparece cuando el matcher de leasing_installment no encontró
 *  cuota, pero SÍ hay 1+ cuotas ya paid cuya diferencia con el importe del
 *  movimiento sugiere que este es un residual sobre una cuota mal cerrada
 *  (típicamente por marcado manual del usuario tras un primer cargo parcial).
 *  Ofrece reabrir como parcial y volver a lanzar el preview para contabilizar
 *  el residual. */
function LeasingResidualReopenSection({
  candidates,
  iban,
  movementUuid,
  busy,
  setBusy,
  setError,
  currency,
  onRefreshPreview,
}: {
  candidates: import("../api").LeasingResidualReopenCandidate[];
  iban: string;
  movementUuid: string;
  busy: boolean;
  setBusy: (b: boolean) => void;
  setError: (m: string | null) => void;
  currency: string;
  onRefreshPreview: () => Promise<void>;
}) {
  void iban;
  void movementUuid;
  return (
    <div
      style={{
        marginTop: 12,
        padding: 12,
        background: "rgba(255, 193, 7, 0.08)",
        border: "1px solid rgba(255, 193, 7, 0.4)",
        borderRadius: 6,
      }}
    >
      <p style={{ margin: "0 0 8px 0", fontSize: "0.9em" }}>
        <strong>¿Es un residual de una cuota ya marcada como pagada?</strong>
        <br />
        <span className="meta" style={{ fontSize: "0.85em" }}>
          Detecté {candidates.length === 1 ? "una cuota cercana" : `${candidates.length} cuotas cercanas`} en{" "}
          <code>status=paid</code> cuya diferencia con este cargo cuadra como residual.
          Reabrir → cambia el status a <code>partial</code> con <code>paidAmount = total − este cargo</code>,
          para que este movimiento se contabilice como residual y cierre la cuota.
        </span>
      </p>
      {candidates.map((c) => (
        <div
          key={`${c.bankCode}-${c.contractNumber}-${c.installmentIndex}`}
          style={{ marginBottom: 6, fontSize: "0.9em", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
        >
          <span>
            Cuota <strong>#{c.installmentIndex}</strong> del {c.installmentDate}
            {" · "}Total {formatMoney(c.totalPayment, currency)}
            {" · "}Nuevo paidAmount {formatMoney(c.suggestedNewPaidAmount, currency)}
            {" · "}Residual {formatMoney(c.residualAmount, currency)}
          </span>
          {!c.hasInvoiceDocEntry && (
            <span className="meta" style={{ fontSize: "0.8em", color: "#c62828" }}>
              ⚠ Sin factura vinculada (marcada a mano). El residual se contabilizará como asiento manual — puede que el motor rechace el flujo residual si la lógica exige factura.
            </span>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={async () => {
              if (
                !confirm(
                  `¿Reabrir cuota #${c.installmentIndex} como parcial (paidAmount=${c.suggestedNewPaidAmount.toFixed(2)}) y refrescar el preview para contabilizar el residual de ${c.residualAmount.toFixed(2)} €?`,
                )
              )
                return;
              setBusy(true);
              setError(null);
              try {
                await reopenLeasingInstallmentAsPartial({
                  bankCode: c.bankCode,
                  contractNumber: c.contractNumber,
                  index: c.installmentIndex,
                  paidAmount: c.suggestedNewPaidAmount,
                });
                await onRefreshPreview();
              } catch (e: unknown) {
                setError(e instanceof Error ? e.message : String(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            Reabrir cuota #{c.installmentIndex} como parcial
          </button>
        </div>
      ))}
    </div>
  );
}
